import { useState, useEffect, useRef, useCallback } from "react";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { G } from "../constants.js";
import { CollabClient, colorFor } from "../collab/CollabClient.js";

const PORTRAIT_CSS = "@media (orientation:landscape){.oracle-portrait-only{display:none!important}}";
const PINCH_BLOCK_CSS = "body{touch-action:none!important;overscroll-behavior:none!important}";

// Fields synced between collaborators (everything except base64 `url` and the
// purely-local `editing` flag). Pure + literal — safe at module scope.
const TRACK_FIELDS = ["x", "y", "w", "type", "tag", "content", "fontSize", "color", "bg", "bold", "italic", "align"];
// True if a hex color is light enough that lime/white lines would vanish on it.
function isLightColor(hex) {
  if (typeof hex !== "string") return false;
  const m = hex.replace("#", "");
  if (m.length < 6) return false;
  const r = parseInt(m.slice(0, 2), 16), g = parseInt(m.slice(2, 4), 16), b = parseInt(m.slice(4, 6), 16);
  // perceived luminance (sRGB)
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 150;
}

// Resolve a link's ordered point list: [A, ...mids, B].
// Ends anchor to their images (offset from top-left, so they follow when moved);
// mids are absolute canvas coords that float. Backward compatible with the old
// [a,b] and [a,b,{adx,ady,bdx,bdy,cx,cy}] shapes.
function linkPoints(link, items) {
  const a = link[0], b = link[1], p = link[2] || null;
  const ia = items.find(i => i.id === a), ib = items.find(i => i.id === b);
  if (!ia || !ib) return null;
  let aOff = {}, bOff = {}, mids = [];
  if (p && p.a) { aOff = p.a; bOff = p.b || {}; mids = p.mids || []; }
  else if (p && (p.adx !== undefined || p.cx !== undefined)) {        // legacy single-bend
    aOff = { dx: p.adx, dy: p.ady }; bOff = { dx: p.bdx, dy: p.bdy };
    mids = (p.cx !== undefined) ? [{ x: p.cx, y: p.cy }] : [];
  }
  const ax = ia.x + (aOff.dx ?? ia.w / 2), ay = ia.y + (aOff.dy ?? 40);
  const bx = ib.x + (bOff.dx ?? ib.w / 2), by = ib.y + (bOff.dy ?? 40);
  return [{ x: ax, y: ay }, ...mids.map(m => ({ x: m.x, y: m.y })), { x: bx, y: by }];
}

// Smooth curve through an ordered point list (Catmull-Rom → cubic bezier).
function smoothPath(pts) {
  if (!pts || pts.length < 2) return "";
  // Sharp straight segments between anchor points (no curve smoothing).
  return "M " + pts.map(p => `${p.x} ${p.y}`).join(" L ");
}
// Same curve as canvas-2d ops (for PNG export). cb(px,py) maps canvas→export coords.
function strokeSmooth(ctx, pts, cb) {
  if (!pts || pts.length < 2) return;
  ctx.beginPath();
  const P = pts.map(p => cb(p.x, p.y));
  ctx.moveTo(P[0].x, P[0].y);
  for (let i = 1; i < P.length; i++) ctx.lineTo(P[i].x, P[i].y); // sharp segments
}
function diffItem(a, b) {
  let patch = null;
  for (const k of TRACK_FIELDS) if (a[k] !== b[k]) (patch || (patch = {}))[k] = b[k];
  return patch;
}
function outgoingItem(it) {
  const { editing, ...rest } = it;   // never broadcast edit-mode (would hijack peers' cursors)
  return { ...rest, editing: false };
}


export default function MoodboardPhase({ items, setItems: setItemsProp, loadedBoard = null, onBack, savedMoodboards, onSaveMoodboard, onLoadMoodboard, onDeleteMoodboard, libraryImages = [], collections = [], user = null, initialRoom = null, token = null }) {
  const { isMobile } = useIsMobile();

  // Moved inside component  -  avoid module-level const TDZ in production bundle
  const SNAP = 22;
  const snap = v => Math.round(v / SNAP) * SNAP;
  const MAX_HISTORY = 30;
  const makeText = (x, y) => ({ id: Date.now() + Math.random(), type:"text", x:snap(x), y:snap(y), w:280, content:"", fontSize:18, color:"#ffffff", bg:"transparent", bold:false, italic:false, align:"left", editing:true });

  const [selected,      setSelected]      = useState([]);
  const [showAddPanel,  setShowAddPanel]  = useState(false);
  const [addSearch,     setAddSearch]     = useState("");
  const [activeAddCol,  setActiveAddCol]  = useState(null); // null = All
  const [links,         setLinksState]    = useState([]);
  const [linkMode,      setLinkMode]      = useState(false);
  const [drawing,       setDrawing]       = useState(null); // { aId, aOff:{dx,dy}, mids:[{x,y}], cursor:{x,y} }
  const [selLink,       setSelLink]       = useState(null); // index of selected link (for pen-tool handles)
  const [showSave,        setShowSave]        = useState(false);
  const [showLoad,        setShowLoad]        = useState(false);
  const [currentMoodId,   setCurrentMoodId]   = useState(null);
  const [exportPreview,   setExportPreview]   = useState(null); // { dataUrl, w, h }
  const [exportBuilding,  setExportBuilding]  = useState(false);
  const [showSizePanel,   setShowSizePanel]   = useState(false);
  const [showBgPanel,     setShowBgPanel]     = useState(false);
  const [renamingMbId,  setRenamingMbId]  = useState(null);
  const [renamingMbVal, setRenamingMbVal] = useState("");
  const [canvasSize,      setCanvasSizeState] = useState({ w: 1920, h: 1080, label: "1920x1080 - Screen" });
  const [boardBg,         setBoardBgState]    = useState("#0a0a18"); // per-board background color (B25; synced B27)
  const CANVAS_PRESETS = [
    { w:1920, h:1080, label:"1920x1080 - Screen" },
    { w:1080, h:1920, label:"1080x1920 - Mobile" },
    { w:1080, h:1080, label:"1080x1080 - Square" },
    { w:2560, h:1440, label:"2560x1440 - 2K" },
    { w:3840, h:2160, label:"3840x2160 - 4K" },
    { w:2480, h:3508, label:"2480x3508 - A4 Portrait" },
  ];
  const [moodName,      setMoodName]      = useState("Moodboard " + new Date().toLocaleDateString());
  const [imgZoom,       setImgZoomState]   = useState({}); // { id: scale }
  const [imgOffset,     setImgOffsetState] = useState({}); // { id: {x,y} } pan offset per image
  const [canvasScale,   setCanvasScale]   = useState(1);  // canvas zoom
  const [history,       setHistory]       = useState([]);  // undo stack
  const [boxSel,        setBoxSel]        = useState(null);

  // Drag state  -  stored in ref to avoid stale closures in mousemove
  const dragRef = useRef(null); // { type:"move"|"resize"|"pan", ids, starts, sx, sw }
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null); // canvas scroll wrapper

  // ─────────────────────────────────────────────────────────────────────────
  // LIVE COLLABORATION (B17)
  // A self-hosted WebSocket relay keeps a shared room state. We sync by
  // wrapping the state setters: local changes emit granular ops; remote ops
  // are applied with an echo-suppression guard. A synchronous "live" mirror
  // ref is the source of truth so rapid drags batch correctly under React 18.
  // ─────────────────────────────────────────────────────────────────────────
  const [live,       setLive]      = useState(false);
  const [peers,      setPeers]     = useState([]);          // [{id,name,color,picture}]
  const [cursors,    setCursors]   = useState({});          // id -> {x,y,name,color,ts}
  const [collabStatus, setCollabStatus] = useState("idle"); // idle|connecting|connected|reconnecting|closed
  const [sessionUrl, setSessionUrl] = useState(null);
  const [showShare,  setShowShare] = useState(false);
  const [copied,     setCopied]    = useState(false);
  const [roomOwnerEmail, setRoomOwnerEmail] = useState(null); // B27: owner of the live board; null = local / own board

  const liveRef          = useRef(false);
  const applyingRemoteRef = useRef(false);
  const clientRef        = useRef(null);
  const selfIdRef        = useRef(null);

  // Synchronous mirrors (always-latest, batch-safe)
  const itemsLiveRef      = useRef(items);
  const linksLiveRef      = useRef(links);
  const imgZoomLiveRef    = useRef(imgZoom);
  const imgOffsetLiveRef  = useRef(imgOffset);
  const canvasSizeLiveRef = useRef(canvasSize);
  const boardBgLiveRef    = useRef(boardBg);
  useEffect(() => { itemsLiveRef.current = items; }, [items]);
  useEffect(() => { linksLiveRef.current = links; }, [links]);
  useEffect(() => { imgZoomLiveRef.current = imgZoom; }, [imgZoom]);
  useEffect(() => { imgOffsetLiveRef.current = imgOffset; }, [imgOffset]);
  useEffect(() => { canvasSizeLiveRef.current = canvasSize; }, [canvasSize]);
  useEffect(() => { boardBgLiveRef.current = boardBg; }, [boardBg]);

  // ── Outbound op coalescer (throttles high-frequency ops like drag/zoom) ──
  const pendingRef = useRef({ updates: new Map(), zoom: null, offset: null, links: null, timer: null });
  const flushPending = useCallback(() => {
    const p = pendingRef.current; p.timer = null;
    const c = clientRef.current;
    if (!c) { p.updates.clear(); p.zoom = p.offset = p.links = null; return; }
    for (const [id, patch] of p.updates) c.op({ k: "update", id, patch });
    p.updates.clear();
    if (p.zoom)   { c.op({ k: "zoommap",   map: p.zoom });   p.zoom = null; }
    if (p.offset) { c.op({ k: "offsetmap", map: p.offset }); p.offset = null; }
    if (p.links)  { c.op({ k: "links",     links: p.links }); p.links = null; }
  }, []);
  const schedule = useCallback(() => {
    const p = pendingRef.current;
    if (!p.timer) p.timer = setTimeout(flushPending, 40);
  }, [flushPending]);
  const emitImmediate = useCallback((op) => { clientRef.current?.op(op); }, []);

  // Diff a local items change and emit add/remove (immediate) + update (throttled)
  const sendItemsDiff = useCallback((prev, next) => {
    const c = clientRef.current; if (!c) return;
    const nextById = new Map(next.map(i => [i.id, i]));
    const prevById = new Map(prev.map(i => [i.id, i]));
    const removed = prev.filter(i => !nextById.has(i.id)).map(i => i.id);
    if (removed.length) c.op({ k: "remove", ids: removed });
    for (const it of next) {
      const p = prevById.get(it.id);
      if (!p) { c.op({ k: "add", item: outgoingItem(it) }); continue; }
      const patch = diffItem(p, it);
      if (patch) {
        const merged = { ...(pendingRef.current.updates.get(it.id) || {}), ...patch };
        pendingRef.current.updates.set(it.id, merged);
        schedule();
      }
    }
  }, [schedule]);

  // ── Wrapped setters (route through mirror + emit ops on local change) ──
  const setItems = useCallback((updater) => {
    const prev = itemsLiveRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;
    itemsLiveRef.current = next;
    setItemsProp(next);
    if (clientRef.current && liveRef.current && !applyingRemoteRef.current) sendItemsDiff(prev, next);
  }, [setItemsProp, sendItemsDiff]);

  const setLinks = useCallback((updater) => {
    const prev = linksLiveRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;
    linksLiveRef.current = next;
    setLinksState(next);
    if (clientRef.current && liveRef.current && !applyingRemoteRef.current) { pendingRef.current.links = next; schedule(); }
  }, [schedule]);

  const setImgZoom = useCallback((updater) => {
    const prev = imgZoomLiveRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;
    imgZoomLiveRef.current = next;
    setImgZoomState(next);
    if (clientRef.current && liveRef.current && !applyingRemoteRef.current) { pendingRef.current.zoom = next; schedule(); }
  }, [schedule]);

  const setImgOffset = useCallback((updater) => {
    const prev = imgOffsetLiveRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;
    imgOffsetLiveRef.current = next;
    setImgOffsetState(next);
    if (clientRef.current && liveRef.current && !applyingRemoteRef.current) { pendingRef.current.offset = next; schedule(); }
  }, [schedule]);

  const setCanvasSize = useCallback((updater) => {
    const prev = canvasSizeLiveRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;
    canvasSizeLiveRef.current = next;
    setCanvasSizeState(next);
    if (clientRef.current && liveRef.current && !applyingRemoteRef.current) emitImmediate({ k: "canvas", w: next.w, h: next.h, label: next.label });
  }, [emitImmediate]);

  // B27: board background is shared state — wrap it like the others so a local
  // change emits a `bg` op, and remote/adopt sets it without echoing back.
  const setBoardBg = useCallback((updater) => {
    const prev = boardBgLiveRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;
    boardBgLiveRef.current = next;
    setBoardBgState(next);
    if (clientRef.current && liveRef.current && !applyingRemoteRef.current) emitImmediate({ k: "bg", bg: next });
  }, [emitImmediate]);

  // Don't let a remote update yank an item I'm actively dragging/editing
  const isBusyLocally = useCallback((id) => {
    const d = dragRef.current;
    if (d) { if (d.ids?.includes(id)) return true; if (d.id != null && d.id === id) return true; }
    const it = itemsLiveRef.current.find(i => i.id === id);
    return !!(it && it.editing);
  }, []);

  // ── Apply a remote op (echo-suppressed via applyingRemoteRef) ──
  const applyRemote = useCallback((op) => {
    applyingRemoteRef.current = true;
    try {
      switch (op.k) {
        case "add":
          if (!itemsLiveRef.current.some(i => i.id === op.item.id)) setItems(prev => [...prev, op.item]);
          break;
        case "update":
          if (isBusyLocally(op.id)) break;
          setItems(prev => prev.map(i => i.id === op.id ? { ...i, ...op.patch } : i));
          break;
        case "remove": {
          const ids = new Set(op.ids || []);
          setItems(prev => prev.filter(i => !ids.has(i.id)));
          setSelected(s => s.filter(x => !ids.has(x)));
          break;
        }
        case "clear":
          setItems([]); setLinks([]); setImgZoom({}); setImgOffset({});
          break;
        case "links":     setLinks(op.links || []); break;
        case "zoommap":   setImgZoom(prev => ({ ...prev, ...(op.map || {}) })); break;
        case "offsetmap": setImgOffset(prev => ({ ...prev, ...(op.map || {}) })); break;
        case "canvas":    setCanvasSize({ w: op.w, h: op.h, label: op.label || "Custom" }); break;
        case "bg":        setBoardBg(op.bg || "#0a0a18"); break;  // B27
        default: break;
      }
    } finally { applyingRemoteRef.current = false; }
  }, [setItems, setLinks, setImgZoom, setImgOffset, setCanvasSize, setBoardBg, isBusyLocally]);

  const adoptState = useCallback((st) => {
    if (!st) return;
    applyingRemoteRef.current = true;
    try {
      setItems(st.items || []);
      setLinks(st.links || []);
      setImgZoom(st.imgZoom || {});
      setImgOffset(st.imgOffset || {});
      if (st.canvasSize) setCanvasSize(st.canvasSize);
      if (st.boardBg)    setBoardBg(st.boardBg);                 // B27: adopt shared bg
      if (st.ownerEmail !== undefined) setRoomOwnerEmail(st.ownerEmail); // B27
      if (st.boardId)    setCurrentMoodId(st.boardId);           // B27: align to owner's board id
      if (st.name)       setMoodName(st.name);
    } finally { applyingRemoteRef.current = false; }
  }, [setItems, setLinks, setImgZoom, setImgOffset, setCanvasSize, setBoardBg]);

  // ── Restore a loaded board's full state (links, pans, canvas size, bg) ──
  // Runs once per load (App hands us a fresh loadedBoard object on each load),
  // so it works whether the board is opened from the grid or the in-board modal.
  const lastLoadedRef = useRef(null);
  useEffect(() => {
    if (!loadedBoard || loadedBoard === lastLoadedRef.current) return;
    lastLoadedRef.current = loadedBoard;
    setLinks(loadedBoard.links || []);
    setImgOffset(loadedBoard.imgOffset || {});
    if (loadedBoard.imgZoom)    setImgZoom(loadedBoard.imgZoom);
    if (loadedBoard.canvasSize) setCanvasSize(loadedBoard.canvasSize);
    if (loadedBoard.boardBg)    setBoardBg(loadedBoard.boardBg);
    setSelLink(null);
    setCurrentMoodId(loadedBoard.id);
    setMoodName(loadedBoard.name || "");
  }, [loadedBoard, setLinks, setImgOffset, setImgZoom, setCanvasSize]);

  // ── Fit the whole artboard into view (with margin) so its bounds are visible ──
  const fitToScreen = useCallback(() => {
    const w = wrapperRef.current; if (!w) return;
    const availW = w.clientWidth - 340, availH = w.clientHeight - 340; // leave room for the 140px margins
    const s = Math.min(availW / canvasSize.w, availH / canvasSize.h);
    setCanvasScale(Math.max(0.1, Math.min(2, s)));
  }, [canvasSize.w, canvasSize.h]);
  const didFitRef = useRef(false);
  useEffect(() => {
    if (didFitRef.current) return; didFitRef.current = true;
    const t = setTimeout(fitToScreen, 80); return () => clearTimeout(t);
  }, [fitToScreen]);

  // ── Session control ──
  const me = useCallback(() => ({
    name:    user?.name || user?.email?.split("@")[0] || "Guest",
    email:   user?.email || null,
    picture: user?.picture || null,
    color:   colorFor(user?.email || user?.name),
  }), [user]);

  // B27: a live room is now a deterministic function of the saved board id, so
  // "Go Live" on board X always yields the same shareable link forever (no more
  // random per-session ids that go stale). Board ids are numeric Date.now()
  // values, already inside the relay's [a-z0-9_-] charset once prefixed.
  const roomForBoard = useCallback((boardId) =>
    "b" + String(boardId).replace(/[^a-z0-9]/gi, "").slice(0, 60), []);

  // B27: am I the owner of the board currently live? When not in a guest-joined
  // session (roomOwnerEmail === null) I'm always editing my own board.
  const myEmail   = user?.email || null;
  const isOwnerNow = roomOwnerEmail === null || (myEmail && roomOwnerEmail === myEmail);

  const connectRoom = useCallback((roomId, asHost, ownerCtx = null) => {
    if (clientRef.current) { try { clientRef.current.close(); } catch {} }
    liveRef.current = true;
    setLive(true);
    setSessionUrl(`${window.location.origin}${window.location.pathname}?room=${roomId}`);
    // Host knows the owner up front; guest learns it from the welcome state.
    setRoomOwnerEmail(ownerCtx ? ownerCtx.ownerEmail : null);

    const client = new CollabClient({
      room: roomId,
      user: me(),
      token,
      handlers: {
        onStatus: (s) => setCollabStatus(s),
        onDenied: (reason) => {
          liveRef.current = false;
          setLive(false); setPeers([]); setCursors({}); setShowShare(false);
          setCollabStatus("idle");
          if (new URLSearchParams(window.location.search).get("room")) {
            window.history.replaceState({}, "", window.location.pathname);
          }
          alert(
            reason === "not_allowlisted" || reason === "wrong_domain"
              ? "This live session is restricted — your account isn't on the allowed list."
              : "Couldn't join the live session — please make sure you're signed in to ORACLE, then try again."
          );
        },
        onWelcome: (m) => {
          selfIdRef.current = m.selfId;
          setPeers(m.peers || []);
          if (m.isFirst && asHost) {
            client.seed({
              items: itemsLiveRef.current,
              links: linksLiveRef.current,
              imgZoom: imgZoomLiveRef.current,
              imgOffset: imgOffsetLiveRef.current,
              canvasSize: canvasSizeLiveRef.current,
              boardBg: boardBgLiveRef.current,                    // B27
              ownerEmail: ownerCtx ? ownerCtx.ownerEmail : null,  // B27
              boardId: ownerCtx ? ownerCtx.boardId : null,        // B27
              name: ownerCtx ? ownerCtx.name : null,              // B27
            });
          } else {
            adoptState(m.state);
          }
        },
        onSync:     (st) => adoptState(st),
        onOp:       (op) => applyRemote(op),
        onPresence: (pl) => {
          setPeers(pl);
          const ids = new Set(pl.map(p => p.id));
          setCursors(c => { const n = {}; for (const k in c) if (ids.has(k)) n[k] = c[k]; return n; });
        },
        onCursor: (from, x, y) => {
          setCursors(c => ({ ...c, [from]: { x, y, ts: Date.now(), color: c[from]?.color, name: c[from]?.name } }));
        },
      },
    });
    clientRef.current = client;
    client.connect();
  }, [me, adoptState, applyRemote, token]);

  const goLive = useCallback(() => {
    // B27: anchor the session to a persistent board id so the share link is
    // stable across sessions and the board has a clear owner (me).
    let boardId = currentMoodId;
    const name  = moodName || "Moodboard";
    if (!boardId) {
      // Unsaved board → give it a real id and persist to my Drive first, so the
      // room is owned and I can reopen the same board + link later.
      boardId = Date.now();
      setCurrentMoodId(boardId);
      try {
        onSaveMoodboard({
          id: boardId, name,
          items: itemsLiveRef.current, links: linksLiveRef.current,
          imgZoom: imgZoomLiveRef.current, imgOffset: imgOffsetLiveRef.current,
          canvasSize: canvasSizeLiveRef.current, boardBg: boardBgLiveRef.current,
          savedAt: new Date().toLocaleDateString(),
        });
      } catch {}
    }
    const roomId = roomForBoard(boardId);
    connectRoom(roomId, true, { ownerEmail: myEmail, boardId, name });
    setShowShare(true);
  }, [connectRoom, currentMoodId, moodName, roomForBoard, myEmail, onSaveMoodboard]);

  const leaveSession = useCallback(() => {
    flushPending();
    try { clientRef.current?.close(); } catch {}
    clientRef.current = null;
    liveRef.current = false;
    setLive(false);
    setPeers([]); setCursors({}); setSessionUrl(null); setShowShare(false);
    setCollabStatus("idle");
    // Drop the ?room= param so a refresh doesn't auto-rejoin
    if (new URLSearchParams(window.location.search).get("room")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [flushPending]);

  const copyLink = useCallback(() => {
    if (!sessionUrl) return;
    try { navigator.clipboard.writeText(sessionUrl); } catch {}
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  }, [sessionUrl]);

  // Auto-join when arriving via a share link
  useEffect(() => {
    if (initialRoom) connectRoom(initialRoom, false);
    return () => { try { clientRef.current?.close(); } catch {} };
  // eslint-disable-next-line
  }, []);

  // B27: while live, the OWNER continuously mirrors the shared board into their
  // own Drive (debounced ~3s) — this is the "it saves to the sheet owner"
  // behaviour. Guests never write to Drive here (isOwnerNow is false for them);
  // their edits live in the relay's persisted room and reach the owner's Drive
  // through this mirror.
  const mirrorTimerRef = useRef(null);
  useEffect(() => {
    if (!live || !isOwnerNow || !currentMoodId) return;
    if (mirrorTimerRef.current) clearTimeout(mirrorTimerRef.current);
    mirrorTimerRef.current = setTimeout(() => {
      try {
        onSaveMoodboard({
          id: currentMoodId, name: moodName || "Moodboard",
          items: itemsLiveRef.current, links: linksLiveRef.current,
          imgZoom: imgZoomLiveRef.current, imgOffset: imgOffsetLiveRef.current,
          canvasSize: canvasSizeLiveRef.current, boardBg: boardBgLiveRef.current,
          savedAt: new Date().toLocaleDateString(),
        });
      } catch {}
    }, 3000);
    return () => { if (mirrorTimerRef.current) clearTimeout(mirrorTimerRef.current); };
  }, [live, isOwnerNow, currentMoodId, moodName, items, links, imgZoom, imgOffset, canvasSize, boardBg, onSaveMoodboard]);

  // Map peer colour/name onto cursors as presence updates arrive
  useEffect(() => {
    setCursors(c => {
      const n = { ...c };
      for (const p of peers) if (n[p.id]) n[p.id] = { ...n[p.id], color: p.color, name: p.name };
      return n;
    });
  }, [peers]);

  // Prune stale cursors (no movement for 6s)
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => {
      setCursors(c => {
        const now = Date.now(); let changed = false; const n = {};
        for (const k in c) { if (now - c[k].ts < 6000) n[k] = c[k]; else changed = true; }
        return changed ? n : c;
      });
    }, 2000);
    return () => clearInterval(t);
  }, [live]);

  const otherPeers = peers.filter(p => p.id !== selfIdRef.current);

  const imageItems = items.filter(i => i.type !== "text");
  const textItems  = items.filter(i => i.type === "text");

  // ── History (undo) ─────────────────────────────────────────────────────
  const pushHistory = useCallback((prevItems) => {
    setHistory(h => [...h.slice(-MAX_HISTORY + 1), prevItems]);
  }, []);

  const undo = useCallback(() => {
    setHistory(h => {
      if (!h.length) return h;
      const prev = h[h.length - 1];
      setItems(prev);
      setSelected([]);
      return h.slice(0, -1);
    });
  }, [setItems]);

  useEffect(() => {
    const handler = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") { e.preventDefault(); undo(); }
      if ((e.key === "Delete" || e.key === "Backspace") && selLink !== null) {
        const tag = (e.target.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;
        e.preventDefault();
        setLinks(ls => ls.filter((_, i) => i !== selLink));
        setSelLink(null);
      }
      if (e.key === "Escape") { if (drawing) setDrawing(null); else setSelLink(null); }
      if (e.key === "Enter" && drawing) { e.preventDefault(); finishLinkDrawAt(drawing.cursor); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, selLink, setLinks, drawing]);

  // ── Mouse events ───────────────────────────────────────────────────────
  useEffect(() => {
    // Touch adapter  -  converts touch events to synthetic mouse events
    const touchToMouse = (te, type) => {
      const t = te.touches[0] || te.changedTouches[0];
      if (!t) return;
      const synth = { clientX: t.clientX, clientY: t.clientY, preventDefault: () => te.preventDefault(), stopPropagation: () => te.stopPropagation() };
      if (type === "move") mv(synth);
      else if (type === "up") up(synth);
    };

    const mv = e => {
      const d = dragRef.current;
      if (!d) return;

      if (d.type === "pan") {
        // Alt+drag: pan image within its frame
        const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
        setImgOffset(o => ({ ...o, [d.id]: { x: d.ox + dx, y: d.oy + dy } }));
        return;
      }

      if (d.type === "move" && canvasRef.current) {
        const r = canvasRef.current.getBoundingClientRect();
        const scale = canvasScale;
        setItems(p => p.map(item => {
          if (!d.ids.includes(item.id)) return item;
          const s = d.starts[item.id];
          return { ...item, x: snap((e.clientX - r.left) / scale - s.ox), y: snap((e.clientY - r.top) / scale - s.oy) };
        }));
      }

      if (d.type === "resize") {
        const delta = e.clientX - d.sx;
        const newW = Math.max(80, snap(d.sw + delta / canvasScale));
        setItems(p => p.map(i => i.id === d.id ? { ...i, w: newW } : i));
      }

      if ((d.type === "linkA" || d.type === "linkB" || d.type === "linkMid") && canvasRef.current) {
        const r = canvasRef.current.getBoundingClientRect();
        const cxp = (e.clientX - r.left) / canvasScale, cyp = (e.clientY - r.top) / canvasScale;
        setLinks(ls => ls.map((lk, idx) => {
          if (idx !== d.li) return lk;
          const a = lk[0], b = lk[1];
          // normalize to the new path shape
          const pts = linkPoints(lk, items);
          const cur = lk[2];
          let np;
          if (cur && cur.a) np = { a: { ...cur.a }, b: { ...cur.b }, mids: (cur.mids || []).map(m => ({ ...m })) };
          else { // legacy → rebuild
            const ia = items.find(i => i.id === a), ib = items.find(i => i.id === b);
            np = { a: { dx: pts[0].x - ia.x, dy: pts[0].y - ia.y }, b: { dx: pts[pts.length-1].x - ib.x, dy: pts[pts.length-1].y - ib.y }, mids: pts.slice(1, -1).map(m => ({ ...m })) };
          }
          const ia = items.find(i => i.id === a), ib = items.find(i => i.id === b);
          if (d.type === "linkA" && ia) np.a = { dx: cxp - ia.x, dy: cyp - ia.y };
          if (d.type === "linkB" && ib) np.b = { dx: cxp - ib.x, dy: cyp - ib.y };
          if (d.type === "linkMid") np.mids[d.k] = { x: cxp, y: cyp };
          return [a, b, np];
        }));
      }

      if (d.type === "boxsel" && canvasRef.current) {
        const r = canvasRef.current.getBoundingClientRect();
        setBoxSel(b => b ? { ...b, ex: (e.clientX - r.left) / canvasScale, ey: (e.clientY - r.top) / canvasScale } : null);
      }
    };

    const up = e => {
      if (dragRef.current?.type === "boxsel" && boxSel) {
        const minX = Math.min(boxSel.sx, boxSel.ex), maxX = Math.max(boxSel.sx, boxSel.ex);
        const minY = Math.min(boxSel.sy, boxSel.ey), maxY = Math.max(boxSel.sy, boxSel.ey);
        if (maxX - minX > 10 || maxY - minY > 10) {
          const inBox = items.filter(item => item.x >= minX && item.x + item.w <= maxX && item.y >= minY).map(i => i.id);
          if (inBox.length) setSelected(inBox);
        }
        setBoxSel(null);
      }
      dragRef.current = null;
    };

    const tmv = e => touchToMouse(e, "move");
    const tup = e => touchToMouse(e, "up");
    window.addEventListener("mousemove", mv, { passive: true });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", tmv, { passive: true });
    window.addEventListener("touchend", tup);
    return () => {
      window.removeEventListener("mousemove", mv);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", tmv);
      window.removeEventListener("touchend", tup);
    };
  }, [canvasScale, boxSel, items, setItems]);

  // ── Native wheel: image zoom without artboard scroll ──────────────────
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const handleWheel = e => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setCanvasScale(s => Math.max(0.3, Math.min(2, s + (e.deltaY > 0 ? -0.08 : 0.08))));
        return;
      }
      // If over an image item: zoom image, block artboard scroll
      const itemDiv = e.target.closest("[data-item-id]");
      if (itemDiv) {
        e.preventDefault();
        const itemId = Number(itemDiv.dataset.itemId);
        setImgZoom(z => ({ ...z, [itemId]: Math.max(0.5, Math.min(4, (z[itemId] || 1) + (e.deltaY > 0 ? -0.1 : 0.1))) }));
      }
      // Elsewhere: natural artboard scroll
    };
    wrapper.addEventListener("wheel", handleWheel, { passive: false });
    return () => wrapper.removeEventListener("wheel", handleWheel);
  }, []);

  // ── Canvas interactions ────────────────────────────────────────────────
  const canvasPt = e => { const r = canvasRef.current.getBoundingClientRect(); return { x: (e.clientX - r.left) / canvasScale, y: (e.clientY - r.top) / canvasScale }; };
  const nearestItemId = (pt, exclude) => {
    let best = null, bd = Infinity;
    items.forEach(it => { if (it.id === exclude) return; const cx = it.x + it.w / 2, cy = it.y + 40; const d = (cx - pt.x) ** 2 + (cy - pt.y) ** 2; if (d < bd) { bd = d; best = it.id; } });
    return best;
  };

  const onCanvasDown = e => {
    if (e.button === 2) return; // right-click handled by contextmenu (finish path)
    if (linkMode) { if (drawing && (e.target === canvasRef.current || e.target?.tagName === "svg" || e.target?.tagName === "path")) addLinkPoint(e); return; }
    if (e.target !== canvasRef.current) return;
    setSelLink(null);
    if (!e.shiftKey) setSelected([]);
    const r = canvasRef.current.getBoundingClientRect();
    const sx = (e.clientX - r.left) / canvasScale, sy = (e.clientY - r.top) / canvasScale;
    dragRef.current = { type:"boxsel" };
    setBoxSel({ sx, sy, ex: sx, ey: sy });
  };

  const onCanvasDbl = e => {
    if (e.target !== canvasRef.current || linkMode) return;
    const r = canvasRef.current.getBoundingClientRect();
    const node = makeText((e.clientX - r.left) / canvasScale - 140, (e.clientY - r.top) / canvasScale - 20);
    pushHistory(items);
    setItems(p => [...p, node]);
    setSelected([node.id]);
  };

  const onCanvasWheel = e => {
    // Handled by native wrapperRef listener  -  this React handler kept for compatibility
  };

  // ── Item interactions ──────────────────────────────────────────────────
  const onItemDown = (e, item) => {
    if (linkMode) return;
    if (item.type === "text" && item.editing) return;
    e.preventDefault(); e.stopPropagation();

    if (e.shiftKey) {
      setSelected(p => p.includes(item.id) ? p.filter(x=>x!==item.id) : [...p, item.id]);
      return;
    }

    // Alt+drag = pan image within its frame (crop position)
    if (e.altKey && item.type === "image") {
      const cur = imgOffset[item.id] || { x: 0, y: 0 };
      dragRef.current = { type: "pan", id: item.id, sx: e.clientX, sy: e.clientY, ox: cur.x, oy: cur.y };
      return;
    }

    const ids = selected.includes(item.id) ? selected : [item.id];
    if (!selected.includes(item.id)) setSelected([item.id]);

    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return;
    const starts = {};
    ids.forEach(id => {
      const it = items.find(i => i.id === id);
      if (it) starts[id] = { ox: (e.clientX - r.left) / canvasScale - it.x, oy: (e.clientY - r.top) / canvasScale - it.y };
    });
    pushHistory(items);
    dragRef.current = { type:"move", ids, starts };
  };

  // onImgWheel handled by native wrapperRef listener (data-item-id detection)
  const onImgWheel = (e, item) => { e.preventDefault(); e.stopPropagation(); };

  const onResizeDown = (e, item) => {
    e.preventDefault(); e.stopPropagation();
    pushHistory(items);
    dragRef.current = { type:"resize", id:item.id, sx:e.clientX, sw:item.w };
  };

  // ── Pen-tool link drawing ──
  const startLinkDraw = (e, item) => {
    const pt = canvasPt(e);
    setDrawing({ aId: item.id, aOff: { dx: pt.x - item.x, dy: pt.y - item.y }, mids: [], cursor: pt });
  };
  const addLinkPoint = (e) => {
    const pt = canvasPt(e);
    setDrawing(d => d ? { ...d, mids: [...d.mids, pt], cursor: pt } : d);
  };
  const onLinkItemDown = (e, item) => {
    if (e.button === 2) return; // right-click finishes via contextmenu
    e.preventDefault(); e.stopPropagation();
    if (!drawing) startLinkDraw(e, item);
    else addLinkPoint(e);
  };
  const finishLinkDrawAt = (pt) => {
    if (!drawing) return;
    const bId = nearestItemId(pt, items.length > 1 ? drawing.aId : null);
    if (bId == null) { setDrawing(null); return; }
    const ib = items.find(i => i.id === bId);
    const path = { a: drawing.aOff, b: { dx: pt.x - ib.x, dy: pt.y - ib.y }, mids: drawing.mids };
    setLinks(l => [...l, [drawing.aId, bId, path]]);
    setDrawing(null);
    setLinkMode(false); // one path per Link activation; toggle again for the next
  };
  const finishLinkDraw = (e) => finishLinkDrawAt(canvasPt(e));
  const onCanvasContextMenu = (e) => {
    if (linkMode && drawing) { e.preventDefault(); finishLinkDraw(e); }
  };

  // ── Duplicate ──────────────────────────────────────────────────────────
  const duplicate = () => {
    if (!selected.length) return;
    const copies = selected.map(id => {
      const orig = items.find(i => i.id === id);
      return orig ? { ...orig, id: Date.now() + Math.random(), x: orig.x + 30, y: orig.y + 30 } : null;
    }).filter(Boolean);
    pushHistory(items);
    setItems(p => [...p, ...copies]);
    setSelected(copies.map(c => c.id));
  };

  // ── Build export canvas  -  returns { dataUrl, w, h } ─────────────────────
  const buildExportCanvas = async () => {
    if (!items.length) return null;

    // Pre-load images to get natural dimensions
    const imgDims = {};
    await Promise.all(imageItems.map(item => new Promise(res => {
      const img = new Image();
      img.onload = () => { imgDims[item.id] = { w: img.naturalWidth, h: img.naturalHeight }; res(); };
      img.onerror = () => { imgDims[item.id] = { w: item.w, h: item.w }; res(); };
      img.src = item.url?.startsWith("data:") ? item.url : "/api/img?url=" + encodeURIComponent(item.url);
    })));

    // True WYSIWYG: export the actual artboard (0,0 → canvasSize) at native
    // resolution, items at their real positions — same empty space as the build.
    // (Capped to MAX_W for file size, preserving aspect.)
    const MAX_W = 3000;
    const scale = Math.min(1, MAX_W / canvasSize.w);
    const offX = 0, offY = 0;
    const canvW = Math.round(canvasSize.w * scale);
    const canvH = Math.round(canvasSize.h * scale);

    const canvas = document.createElement("canvas");
    canvas.width = canvW; canvas.height = canvH;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = boardBg; ctx.fillRect(0, 0, canvW, canvH);
    ctx.fillStyle = "rgba(255,255,255,.035)";
    for (let x = 0; x < canvW; x += 22) for (let y = 0; y < canvH; y += 22) {
      ctx.beginPath(); ctx.arc(x, y, 1, 0, Math.PI*2); ctx.fill();
    }

    const wx = x => (x - offX) * scale;
    const wy = y => (y - offY) * scale;
    const ws = v => v * scale;

    links.forEach((lk) => {
      const pts = linkPoints(lk, items);
      if (!pts) return;
      strokeSmooth(ctx, pts, (px, py) => ({ x: wx(px), y: wy(py) }));
      ctx.strokeStyle = isLightColor(boardBg) ? "#171733" : "rgba(232,255,71,.6)"; ctx.lineWidth = 2; ctx.stroke();
    });

    await Promise.all(imageItems.map(item => new Promise(res => {
      const isDataUri = item.url?.startsWith("data:");
      const img = new Image();

      img.onload = () => {
        const zoom   = imgZoom[item.id] || 1;
        const pan    = imgOffset?.[item.id] || { x: 0, y: 0 };
        const dims   = imgDims[item.id] || { w: img.naturalWidth || item.w, h: img.naturalHeight || item.w };
        const frameW = ws(item.w);
        const frameH = ws(item.w * (dims.h / dims.w || 1));
        const drawW  = frameW * zoom, drawH = frameH * zoom;
        ctx.save();
        const r = ws(7), ix = wx(item.x), iy = wy(item.y);
        // Rounded rect clip
        ctx.beginPath();
        ctx.moveTo(ix+r, iy); ctx.lineTo(ix+frameW-r, iy); ctx.arcTo(ix+frameW, iy, ix+frameW, iy+r, r);
        ctx.lineTo(ix+frameW, iy+frameH-r); ctx.arcTo(ix+frameW, iy+frameH, ix+frameW-r, iy+frameH, r);
        ctx.lineTo(ix+r, iy+frameH); ctx.arcTo(ix, iy+frameH, ix, iy+frameH-r, r);
        ctx.lineTo(ix, iy+r); ctx.arcTo(ix, iy, ix+r, iy, r);
        ctx.closePath(); ctx.clip();
        const maxPan = frameW * (zoom-1)/2;
        const cx = Math.max(-maxPan, Math.min(maxPan, pan.x * scale));
        const cy = Math.max(-maxPan, Math.min(maxPan, pan.y * scale));
        try {
          ctx.drawImage(img, ix + (frameW-drawW)/2 + cx, iy + (frameH-drawH)/2 + cy, drawW, drawH);
        } catch(e) {
          // Canvas tainted  -  draw placeholder
          ctx.fillStyle = "rgba(255,255,255,.06)";
          ctx.fillRect(ix, iy, frameW, frameH);
          ctx.fillStyle = "rgba(255,255,255,.2)";
          ctx.font = `${ws(11)}px DM Mono,monospace`;
          ctx.textAlign = "center";
          ctx.fillText("Web image", ix + frameW/2, iy + frameH/2);
        }
        ctx.restore(); res();
      };

      img.onerror = () => {
        // Draw placeholder for images that fail to load
        const dims  = imgDims[item.id] || { w: item.w, h: item.w };
        const frameW = ws(item.w), frameH = ws(item.w * (dims.h / dims.w || 1));
        ctx.save();
        ctx.fillStyle = "rgba(255,255,255,.05)";
        ctx.fillRect(wx(item.x), wy(item.y), frameW, frameH);
        ctx.restore();
        res();
      };

      // Data URIs load directly. Remote URLs go through the CORS proxy so the
      // canvas isn't tainted and they actually render in the PNG (B25 fix).
      if (isDataUri) {
        img.src = item.url;
      } else {
        img.crossOrigin = "anonymous";
        img.src = "/api/img?url=" + encodeURIComponent(item.url);
      }
    })));

    textItems.forEach(t => {
      if (!t.content) return;
      ctx.save();
      const fs = ws(t.fontSize), lh = fs * 2.2;
      const lines = t.content.split("\n");
      if (t.bg && t.bg !== "transparent") {
        ctx.fillStyle = t.bg;
        ctx.fillRect(wx(t.x), wy(t.y), ws(t.w || 300), lines.length * lh + ws(16));
      }
      ctx.font = `${t.italic?"italic ":""}${t.bold?"bold ":""}${fs}px DM Sans,sans-serif`;
      ctx.fillStyle = t.color || "#fff"; ctx.textAlign = t.align || "left";
      const ax = t.align==="center" ? wx(t.x)+ws(t.w)/2 : t.align==="right" ? wx(t.x)+ws(t.w) : wx(t.x);
      lines.forEach((line, li) => ctx.fillText(line, ax, wy(t.y) + (li+1)*lh));
      ctx.restore();
    });

    // toDataURL fails if canvas is tainted by cross-origin images  -  use blob fallback
    let dataUrl;
    try {
      dataUrl = canvas.toDataURL("image/png", .95);
    } catch(e) {
      // Fallback: use toBlob via createObjectURL
      dataUrl = await new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
          if (blob) resolve(URL.createObjectURL(blob));
          else reject(new Error("Canvas export failed"));
        }, "image/png", .95);
      });
    }
    return { dataUrl, w: canvW, h: canvH };
  };

  // ── Export button click  -  build then show preview modal ───────────────
  const handleExport = async () => {
    if (!items.length || exportBuilding) return;
    setExportBuilding(true);
    try {
      const result = await buildExportCanvas();
      if (result) {
        setExportPreview(result);
      } else {
        alert("Nothing to export  -  add images or text to the moodboard first.");
      }
    } catch(err) {
      console.error("Export failed:", err);
      alert("Export failed: " + (err?.message || "unknown error") + "\n\nIf you have web-saved images (not downloaded), try deleting them from the moodboard  -  only desktop-uploaded images can be exported.");
    } finally {
      setExportBuilding(false);
    }
  };

    const upd = (id, patch) => setItems(p => p.map(i => i.id===id ? {...i,...patch} : i));
  const selText = selected.length===1 ? items.find(i=>i.id===selected[0]&&i.type==="text") : null;
  const boxRect = boxSel ? { left:Math.min(boxSel.sx,boxSel.ex), top:Math.min(boxSel.sy,boxSel.ey), width:Math.abs(boxSel.ex-boxSel.sx), height:Math.abs(boxSel.ey-boxSel.sy) } : null;
  // Link colors auto-adapt to the board background so lines stay visible (B26)
  const lightBoard   = isLightColor(boardBg);
  const linkSel      = lightBoard ? "#171733" : "#E8FF47";
  const linkSoft     = lightBoard ? "rgba(23,23,51,0.65)" : "rgba(232,255,71,0.5)";
  const linkHandleBg = lightBoard ? "#ffffff" : "#060610";

  return (
    <div style={{ height:"100vh", background:"#060610", display:"flex", flexDirection:"column", fontFamily:"'DM Sans',sans-serif", overflow:"hidden" }}>
      <style>{G}</style>

      {/* ── Header ── */}
      <div style={{ padding:"10px 16px", borderBottom:"1px solid rgba(255,255,255,.05)", background:"rgba(7,7,16,.98)", backdropFilter:"blur(20px)", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, gap:"6px", flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
          <button onClick={onBack} style={{ padding:"6px 12px", borderRadius:"6px", border:"1px solid rgba(255,255,255,.09)", background:"transparent", color:"rgba(255,255,255,.45)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer" }}>← Back</button>
          <span style={{ fontFamily:"'Bebas Neue'", fontSize:"20px", letterSpacing:".14em", color:"#E8FF47" }}>MOODBOARD</span>
          <span style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.18)" }}>
            {items.length} items{selected.length>0?` - ${selected.length} selected`:""}{ !isMobile ? " - Ctrl+Z undo - dbl-click = text - scroll = zoom - Alt+drag = pan" : " - Tap to select - Drag to move"}
          </span>

          {/* ── Live collaboration ── */}
          {!live ? (
            <button onClick={goLive} title="Start a live session" style={{ display:"flex", alignItems:"center", gap:"6px", padding:"5px 11px", borderRadius:"6px", border:"1px solid rgba(94,211,243,.4)", background:"rgba(94,211,243,.08)", color:"#5ED3F3", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".07em", textTransform:"uppercase", cursor:"pointer", whiteSpace:"nowrap" }}>
              <span style={{ width:"7px", height:"7px", borderRadius:"50%", background:"#5ED3F3", display:"inline-block" }}/>
              Go Live
            </button>
          ) : (
            <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"5px", padding:"4px 10px", borderRadius:"20px", background:"rgba(124,246,169,.1)", border:"1px solid rgba(124,246,169,.3)" }}>
                <span style={{ width:"7px", height:"7px", borderRadius:"50%", background: collabStatus==="connected"?"#7CF6A9":"#FFB155", animation:"pulse 1.5s ease-in-out infinite", display:"inline-block" }}/>
                <span style={{ fontFamily:"'DM Mono'", fontSize:"9px", letterSpacing:".12em", color: collabStatus==="connected"?"#7CF6A9":"#FFB155" }}>
                  {collabStatus==="connected"?"LIVE":collabStatus==="reconnecting"?"RECONNECTING":"CONNECTING"}
                </span>
              </div>
              <div style={{ display:"flex", alignItems:"center" }}>
                {peers.slice(0,5).map((p,idx)=>(
                  <div key={p.id} title={p.name + (p.id===selfIdRef.current?" (you)":"")} style={{ width:"22px", height:"22px", borderRadius:"50%", marginLeft: idx?"-6px":0, border:`2px solid ${p.color}`, background:"#0b0b1c", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 0 0 1px #060610", flexShrink:0 }}>
                    <span style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:p.color, fontWeight:600 }}>{(p.name||"?").slice(0,1).toUpperCase()}</span>
                  </div>
                ))}
                {peers.length>5 && <span style={{ marginLeft:"5px", fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.4)" }}>+{peers.length-5}</span>}
              </div>
              <button onClick={()=>setShowShare(true)} style={{ padding:"5px 10px", borderRadius:"6px", border:"1px solid rgba(94,211,243,.35)", background:"rgba(94,211,243,.07)", color:"#5ED3F3", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>Share</button>
              <button onClick={leaveSession} style={{ padding:"5px 10px", borderRadius:"6px", border:"1px solid rgba(255,80,80,.25)", background:"transparent", color:"rgba(255,120,120,.7)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>Leave</button>
            </div>
          )}
        </div>

        <div style={{ display:"flex", gap:"5px", alignItems:"center", flexWrap:"wrap" }}>
          {/* Text toolbar */}
          {selText && (
            <div style={{ display:"flex", gap:"3px", alignItems:"center", padding:"3px 8px", borderRadius:"8px", background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.08)" }}>
              <select value={selText.fontSize} onChange={e=>upd(selText.id,{fontSize:Number(e.target.value)})} style={{ background:"transparent", border:"none", color:"#fff", fontFamily:"'DM Mono'", fontSize:"10px", outline:"none", cursor:"pointer", width:"40px" }}>
                {[10,12,14,16,18,20,24,28,32,36,42,48,56,64,72].map(s=><option key={s} value={s} style={{ background:"#0b0b1c" }}>{s}</option>)}
              </select>
              <div style={{ width:"1px", height:"14px", background:"rgba(255,255,255,.1)", margin:"0 2px" }}/>
              <button onClick={()=>upd(selText.id,{bold:!selText.bold})} style={{ padding:"2px 6px", borderRadius:"3px", border:"none", background:selText.bold?"rgba(232,255,71,.15)":"transparent", color:selText.bold?"#E8FF47":"rgba(255,255,255,.5)", fontWeight:"bold", fontSize:"11px", cursor:"pointer" }}>B</button>
              <button onClick={()=>upd(selText.id,{italic:!selText.italic})} style={{ padding:"2px 6px", borderRadius:"3px", border:"none", background:selText.italic?"rgba(232,255,71,.15)":"transparent", color:selText.italic?"#E8FF47":"rgba(255,255,255,.5)", fontStyle:"italic", fontSize:"11px", cursor:"pointer" }}>I</button>
              <div style={{ width:"1px", height:"14px", background:"rgba(255,255,255,.1)", margin:"0 2px" }}/>
              {["left","center","right"].map(a=>(
                <button key={a} onClick={()=>upd(selText.id,{align:a})} style={{ padding:"2px 5px", borderRadius:"3px", border:"none", background:selText.align===a?"rgba(232,255,71,.15)":"transparent", color:selText.align===a?"#E8FF47":"rgba(255,255,255,.4)", fontSize:"9px", cursor:"pointer" }}>
                  {a==="left"?"⬅":a==="center"?"☰":"➡"}
                </button>
              ))}
              <div style={{ width:"1px", height:"14px", background:"rgba(255,255,255,.1)", margin:"0 2px" }}/>
              <span style={{ fontFamily:"'DM Mono'", fontSize:"8px", color:"rgba(255,255,255,.3)", letterSpacing:".06em" }}>TXT</span>
              {["#ffffff","#E8FF47","#ff6b6b","#6bffb8","#6bb8ff","#ffb86b","#000000"].map(c=>(
                <div key={c} onClick={()=>upd(selText.id,{color:c})} style={{ width:"13px", height:"13px", borderRadius:"50%", background:c, cursor:"pointer", border:selText.color===c?"2px solid #E8FF47":"1px solid rgba(255,255,255,.2)", flexShrink:0 }}/>
              ))}
              <input type="color" value={selText.color} onChange={e=>upd(selText.id,{color:e.target.value})} style={{ width:"16px", height:"16px", borderRadius:"3px", border:"none", background:"transparent", cursor:"pointer", padding:0 }}/>
              <div style={{ width:"1px", height:"14px", background:"rgba(255,255,255,.1)", margin:"0 2px" }}/>
              <span style={{ fontFamily:"'DM Mono'", fontSize:"8px", color:"rgba(255,255,255,.3)", letterSpacing:".06em" }}>BG</span>
              {["transparent","rgba(0,0,0,0.7)","rgba(7,7,16,0.9)","rgba(232,255,71,0.15)","rgba(255,255,255,0.1)"].map((c,i)=>(
                <div key={i} onClick={()=>upd(selText.id,{bg:c})} style={{ width:"13px", height:"13px", borderRadius:"3px", background:c==="transparent"?"repeating-conic-gradient(#808080 0% 25%,transparent 0% 50%) 0 0/6px 6px":c, cursor:"pointer", border:selText.bg===c?"2px solid #E8FF47":"1px solid rgba(255,255,255,.2)", flexShrink:0 }}/>
              ))}
            </div>
          )}

          {/* Canvas zoom */}
          <div style={{ display:"flex", gap:"2px", alignItems:"center", padding:"4px 8px", borderRadius:"6px", background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.07)" }}>
            <button onClick={()=>setCanvasScale(s=>Math.max(0.3,s-.1))} style={{ background:"none", border:"none", color:"rgba(255,255,255,.5)", cursor:"pointer", fontSize:"14px", lineHeight:1, padding:"0 2px" }}>−</button>
            <span style={{ fontFamily:"'DM Mono'", fontSize:"10px", color:"rgba(255,255,255,.4)", width:"34px", textAlign:"center" }}>{Math.round(canvasScale*100)}%</span>
            <button onClick={()=>setCanvasScale(s=>Math.min(2,s+.1))} style={{ background:"none", border:"none", color:"rgba(255,255,255,.5)", cursor:"pointer", fontSize:"14px", lineHeight:1, padding:"0 2px" }}>+</button>
            <button onClick={fitToScreen} style={{ background:"none", border:"none", color:"rgba(255,255,255,.25)", cursor:"pointer", fontFamily:"'DM Mono'", fontSize:"9px", padding:"0 2px" }}>fit</button>
          </div>

          <button onClick={()=>{ const node=makeText(120,80); pushHistory(items); setItems(p=>[...p,node]); setSelected([node.id]); }} style={{ padding:"5px 10px", borderRadius:"6px", border:"1px solid rgba(255,255,255,.1)", background:"transparent", color:"rgba(255,255,255,.5)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>+ Text</button>

          {selected.length > 0 && <button onClick={duplicate} style={{ padding:"5px 10px", borderRadius:"6px", border:"1px solid rgba(255,255,255,.1)", background:"transparent", color:"rgba(255,255,255,.4)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>Duplicate</button>}

          <button onClick={undo} disabled={!history.length} style={{ padding:"5px 10px", borderRadius:"6px", border:"1px solid rgba(255,255,255,.1)", background:"transparent", color:history.length?"rgba(255,255,255,.4)":"rgba(255,255,255,.15)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:history.length?"pointer":"default" }}>↩ Undo</button>

          {/* Canvas size: inline editable W x H + preset picker */}
          {!isMobile && (
            <div style={{ display:"flex", alignItems:"center", gap:"4px", marginLeft:"4px", padding:"4px 7px", borderRadius:"6px", background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.1)" }}>
              <input
                className="mb-dim" type="number" value={canvasSize.w} min="400" max="7680"
                onChange={e => { const v = parseInt(e.target.value); if(v>=400) setCanvasSize(s=>({...s,w:v,label:"Custom"})); }}
                style={{ width:"38px", background:"transparent", border:"none", padding:"1px 0", color:"rgba(255,255,255,.5)", fontFamily:"'DM Mono'", letterSpacing:".04em", outline:"none", textAlign:"center" }}
              />
              <span style={{ color:"rgba(255,255,255,.22)", fontFamily:"'DM Mono'", fontSize:"10px" }}>×</span>
              <input
                className="mb-dim" type="number" value={canvasSize.h} min="400" max="7680"
                onChange={e => { const v = parseInt(e.target.value); if(v>=400) setCanvasSize(s=>({...s,h:v,label:"Custom"})); }}
                style={{ width:"38px", background:"transparent", border:"none", padding:"1px 0", color:"rgba(255,255,255,.5)", fontFamily:"'DM Mono'", letterSpacing:".04em", outline:"none", textAlign:"center" }}
              />
              <button onClick={()=>setShowSizePanel(s=>!s)} title="Canvas presets" style={{ padding:"2px 4px", borderRadius:"4px", border:"none", background:showSizePanel?"rgba(232,255,71,.12)":"transparent", color:showSizePanel?"#E8FF47":"rgba(255,255,255,.3)", cursor:"pointer", fontSize:"9px", lineHeight:1 }}>▾</button>
            </div>
          )}

          {/* Board background color (B25) */}
          <button onClick={()=>setShowBgPanel(s=>!s)} title="Board background color" style={{ display:"flex", alignItems:"center", gap:"5px", padding:"4px 8px", borderRadius:"6px", border:showBgPanel?"1px solid rgba(232,255,71,.5)":"1px solid rgba(255,255,255,.1)", background:showBgPanel?"rgba(232,255,71,.1)":"transparent", color:"rgba(255,255,255,.4)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>
            <span style={{ width:"13px", height:"13px", borderRadius:"3px", background:boardBg, border:"1px solid rgba(255,255,255,.35)", flexShrink:0 }}/>
            {!isMobile && "BG"}
          </button>

          <button onClick={()=>{setLinkMode(m=>!m);setDrawing(null);setSelLink(null);}} style={{ padding:"5px 10px", borderRadius:"6px", cursor:"pointer", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", border:linkMode?"1px solid rgba(232,255,71,.5)":"1px solid rgba(255,255,255,.1)", background:linkMode?"rgba(232,255,71,.1)":"transparent", color:linkMode?"#E8FF47":"rgba(255,255,255,.4)" }}>
            {linkMode?(drawing?"click=point · right-click=finish":"click an image to start"):"Link"}
          </button>

          {links.length>0 && <button onClick={()=>setLinks([])} style={{ padding:"5px 10px", borderRadius:"6px", border:"1px solid rgba(255,80,80,.2)", background:"transparent", color:"rgba(255,100,100,.5)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>Clear Links</button>}

          {selected.length>0 && (
            <button onClick={()=>{ pushHistory(items); setItems(p=>p.filter(i=>!selected.includes(i.id))); setSelected([]); }} style={{ padding:"5px 10px", borderRadius:"6px", border:"1px solid rgba(255,80,80,.25)", background:"rgba(255,60,60,.07)", color:"rgba(255,120,120,.8)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>
              Remove{selected.length>1?` (${selected.length})`:""}
            </button>
          )}

          {savedMoodboards?.length>0 && <button onClick={()=>setShowLoad(true)} style={{ padding:"5px 10px", borderRadius:"6px", border:"1px solid rgba(255,255,255,.1)", background:"transparent", color:"rgba(255,255,255,.4)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>Load ({savedMoodboards.length})</button>}
          <button onClick={()=>{setShowAddPanel(true);setAddSearch("");}} style={{ padding:"5px 12px", borderRadius:"6px", border:"1px solid rgba(232,255,71,.4)", background:"rgba(232,255,71,.1)", color:"#E8FF47", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>{isMobile?"+ Add":"+ Add Images"}</button>
          <button onClick={()=>setShowSave(true)} style={{ padding:"5px 12px", borderRadius:"6px", border:"1px solid rgba(232,255,71,.3)", background:"rgba(232,255,71,.06)", color:"#E8FF47", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>Save</button>
          <button onClick={handleExport} disabled={exportBuilding} style={{ padding:"5px 14px", borderRadius:"6px", border:"none", background:exportBuilding?"rgba(232,255,71,.4)":"#E8FF47", color:"#070710", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".08em", textTransform:"uppercase", cursor:exportBuilding?"wait":"pointer", fontWeight:"500", transition:"background .2s" }}>{exportBuilding?"...":isMobile?"Export":"Export PNG"}</button>
        </div>
      </div>

      {/* ── Board Background Panel (page-level so it isn't clipped by the header) ── */}
      {showBgPanel && (
        <>
          <div onClick={()=>setShowBgPanel(false)} style={{ position:"fixed", inset:0, zIndex:240 }}/>
          <div onClick={e=>e.stopPropagation()} style={{ position:"absolute", top:"44px", left:isMobile?"12px":"auto", right:isMobile?"auto":"230px", zIndex:250, background:"#0d0d20", border:"1px solid rgba(255,255,255,.12)", borderRadius:"10px", padding:"12px", boxShadow:"0 12px 40px rgba(0,0,0,.8)", width:"210px" }}>
            <p style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.35)", letterSpacing:".1em", textTransform:"uppercase", marginBottom:"10px" }}>Board background</p>
            <div style={{ display:"flex", flexWrap:"wrap", gap:"7px", marginBottom:"12px" }}>
              {[
                { c:"#0a0a18", n:"Dark" }, { c:"#060610", n:"Black" }, { c:"#16162a", n:"Navy" },
                { c:"#ffffff", n:"White" }, { c:"#f2f0ea", n:"Paper" }, { c:"#1c1c1c", n:"Charcoal" },
                { c:"#13241c", n:"Forest" }, { c:"#241320", n:"Plum" },
              ].map(({c,n})=>(
                <button key={c} title={n} onClick={()=>setBoardBg(c)} style={{ width:"30px", height:"30px", borderRadius:"6px", background:c, cursor:"pointer", border:boardBg===c?"2px solid #E8FF47":"1px solid rgba(255,255,255,.2)", flexShrink:0 }}/>
              ))}
            </div>
            <label style={{ display:"flex", alignItems:"center", gap:"8px", fontFamily:"'DM Mono'", fontSize:"10px", color:"rgba(255,255,255,.5)" }}>
              Custom
              <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(boardBg)?boardBg:"#0a0a18"} onChange={e=>setBoardBg(e.target.value)} style={{ width:"40px", height:"26px", border:"none", background:"none", cursor:"pointer", padding:0 }}/>
            </label>
          </div>
        </>
      )}

      {/* ── Canvas Size Panel ── */}
      {showSizePanel && (
        <div style={{ position:"absolute", top:"44px", left:isMobile?"12px":"auto", right:isMobile?"auto":"120px", zIndex:200, background:"#0d0d20", border:"1px solid rgba(255,255,255,.1)", borderRadius:"10px", padding:"8px", boxShadow:"0 12px 40px rgba(0,0,0,.8)", minWidth:"220px" }}>
          <p style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.3)", letterSpacing:".1em", textTransform:"uppercase", padding:"4px 8px 8px" }}>Canvas Size</p>
          {CANVAS_PRESETS.map(p => (
            <button key={p.label} onClick={()=>{ setCanvasSize(p); setShowSizePanel(false); }}
              style={{ display:"block", width:"100%", textAlign:"left", padding:"8px 10px", borderRadius:"7px", border:"none", background:canvasSize.label===p.label?"rgba(232,255,71,.1)":"transparent", color:canvasSize.label===p.label?"#E8FF47":"rgba(255,255,255,.55)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".04em", cursor:"pointer", transition:"background .1s" }}>
              {p.label}
            </button>
          ))}
          <div style={{ borderTop:"1px solid rgba(255,255,255,.07)", marginTop:"6px", padding:"8px 8px 4px" }}>
            <p style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.25)", letterSpacing:".08em", marginBottom:"6px" }}>Custom</p>
            <div style={{ display:"flex", gap:"6px", alignItems:"center" }}>
              <input type="number" defaultValue={canvasSize.w} min="400" max="7680"
                onBlur={e=>setCanvasSize(s=>({...s, w:Math.max(400,parseInt(e.target.value)||s.w), label:"Custom"}))}
                style={{ width:"70px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:"5px", padding:"5px 6px", color:"#fff", fontFamily:"'DM Mono'", fontSize:"10px", outline:"none" }}/>
              <span style={{ color:"rgba(255,255,255,.3)", fontFamily:"'DM Mono'", fontSize:"10px" }}>×</span>
              <input type="number" defaultValue={canvasSize.h} min="400" max="7680"
                onBlur={e=>setCanvasSize(s=>({...s, h:Math.max(400,parseInt(e.target.value)||s.h), label:"Custom"}))}
                style={{ width:"70px", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.12)", borderRadius:"5px", padding:"5px 6px", color:"#fff", fontFamily:"'DM Mono'", fontSize:"10px", outline:"none" }}/>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Images Panel ── */}
      {showAddPanel && (
        <div onClick={()=>setShowAddPanel(false)} style={{ position:"fixed", inset:0, zIndex:700, background:"rgba(0,0,0,.75)", backdropFilter:"blur(12px)", display:"flex", alignItems:"flex-start", justifyContent:"flex-end", animation:"fade-in .2s ease" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"340px", height:"100%", background:"#0a0a1b", borderLeft:"1px solid rgba(255,255,255,.07)", display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <style>{".oracle-add-hover{background:rgba(0,0,0,0);transition:background .15s}.oracle-add-hover:hover{background:rgba(0,0,0,.55)}.oracle-add-hover span{opacity:0;transition:opacity .15s}.oracle-add-hover:hover span{opacity:1}"}</style>
            {/* Panel header */}
            <div style={{ padding:"14px 16px 10px", borderBottom:"1px solid rgba(255,255,255,.06)", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
              <span style={{ fontFamily:"'Bebas Neue'", fontSize:"18px", letterSpacing:".14em", color:"#E8FF47" }}>ADD IMAGES</span>
              <button onClick={()=>setShowAddPanel(false)} style={{ background:"transparent", border:"none", color:"rgba(255,255,255,.35)", fontSize:"18px", cursor:"pointer", lineHeight:1 }}>×</button>
            </div>
            {/* Collection filter tabs */}
            {collections.length > 0 && (
              <div style={{ display:"flex", gap:"4px", padding:"0 12px 8px", overflowX:"auto", flexShrink:0, borderBottom:"1px solid rgba(255,255,255,.06)" }}>
                {[{id:null,label:"All"}, ...collections.map(c=>({id:c.id,label:c.name}))].map(tab => (
                  <button key={String(tab.id)} onClick={()=>setActiveAddCol(tab.id)}
                    style={{ padding:"4px 10px", borderRadius:"5px", border:"none", background:activeAddCol===tab.id?"rgba(232,255,71,.15)":"rgba(255,255,255,.05)", color:activeAddCol===tab.id?"#E8FF47":"rgba(255,255,255,.4)", fontFamily:"'DM Mono'", fontSize:"9px", letterSpacing:".07em", textTransform:"uppercase", cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
            {/* Search */}
            <div style={{ padding:"10px 14px", flexShrink:0 }}>
              <input
                value={addSearch}
                onChange={e=>setAddSearch(e.target.value)}
                placeholder="Search library..."
                autoFocus
                style={{ width:"100%", background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.1)", borderRadius:"7px", padding:"8px 12px", color:"#fff", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".05em", outline:"none", boxSizing:"border-box" }}
              />
            </div>
            {/* Image grid */}
            <div style={{ flex:1, overflowY:"auto", padding:"0 12px 12px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px", alignContent:"start" }}>
              {(() => {
                let pool = libraryImages;
                if (activeAddCol !== null) {
                  const col = collections.find(c => c.id === activeAddCol);
                  pool = col ? libraryImages.filter(i => col.imageIds?.includes(i.id)) : libraryImages;
                }
                const filtered = addSearch
                  ? pool.filter(i => [i.tag,...(i.tags||[]),i.source||""].some(t=>(t||"").toLowerCase().includes(addSearch.toLowerCase())))
                  : pool;
                return filtered.map(img => {
                const alreadyIn = items.some(it => it.id === img.id);
                return (
                  <div
                    key={img.id}
                    onClick={()=>{
                      if (alreadyIn) return;
                      // Find a non-overlapping spot: scan in a grid pattern, skip occupied cells
                      const W = 260, H = 200; // cell size
                      const imageItems = items.filter(i=>i.type!=="text");
                      let placed = false;
                      let newX = 60, newY = 60;
                      for (let row = 0; row < 10 && !placed; row++) {
                        for (let col = 0; col < 4 && !placed; col++) {
                          const cx = 60 + col * W;
                          const cy = 60 + row * H;
                          const occupied = imageItems.some(it =>
                            Math.abs(it.x - cx) < W * 0.75 && Math.abs(it.y - cy) < H * 0.75
                          );
                          if (!occupied) { newX = cx; newY = cy; placed = true; }
                        }
                      }
                      if (!placed) { // all slots full, scatter below
                        newX = 60 + (imageItems.length % 4) * W;
                        newY = 60 + Math.floor(imageItems.length / 4) * H;
                      }
                      const newItem = { ...img, type:"image", x:newX, y:newY, w:240 };
                      pushHistory(items);
                      setItems(p=>[...p, newItem]);
                    }}
                    style={{ position:"relative", aspectRatio:"1", borderRadius:"8px", overflow:"hidden", cursor:alreadyIn?"default":"pointer", opacity:alreadyIn?0.35:1, border:alreadyIn?"1px solid rgba(232,255,71,.3)":"1px solid rgba(255,255,255,.08)", transition:"border-color .15s, opacity .15s" }}
                  >
                    <img
                      src={img.url}
                      alt={img.tag}
                      style={{ width:"100%", height:"100%", objectFit:"cover" }}
                      onError={e=>{ e.target.style.opacity="0.2"; }}
                    />
                    {alreadyIn && (
                      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,.4)" }}>
                        <span style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"#E8FF47", letterSpacing:".1em" }}>ADDED</span>
                      </div>
                    )}
                    {!alreadyIn && (
                      <div className="oracle-add-hover" style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <span style={{ fontFamily:"'DM Mono'", fontSize:"22px", fontWeight:"300", color:"#E8FF47" }}>+</span>
                      </div>
                    )}
                  </div>
                );
              })})()}
              {libraryImages.length === 0 && (
                <div style={{ gridColumn:"span 2", textAlign:"center", padding:"40px 0", fontFamily:"'DM Mono'", fontSize:"10px", color:"rgba(255,255,255,.2)", letterSpacing:".1em" }}>
                  No images in library yet
                </div>
              )}
            </div>
            <div style={{ padding:"12px 16px", borderTop:"1px solid rgba(255,255,255,.06)", flexShrink:0, fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.22)", textAlign:"center", letterSpacing:".06em" }}>
              CLICK IMAGE TO ADD - CLICK OUTSIDE TO CLOSE
            </div>
          </div>
        </div>
      )}

      {/* ── Canvas wrapper ── */}
      <div ref={wrapperRef} style={{ flex:1, overflow:"auto", position:"relative", cursor:"default", touchAction:isMobile?"pan-x pan-y":"auto", background:"#141420", display:"flex" }} onWheel={onCanvasWheel}
        onContextMenu={onCanvasContextMenu}
        onMouseMove={e => {
          if (drawing && canvasRef.current) {
            const r = canvasRef.current.getBoundingClientRect();
            const pt = { x:(e.clientX - r.left)/canvasScale, y:(e.clientY - r.top)/canvasScale };
            setDrawing(d => d ? { ...d, cursor: pt } : d);
          }
          if (!liveRef.current || !canvasRef.current) return;
          const r = canvasRef.current.getBoundingClientRect();
          clientRef.current?.cursor((e.clientX - r.left) / canvasScale, (e.clientY - r.top) / canvasScale);
        }}
      >
        {/* Stage: sized to the scaled artboard so it centers + scrolls correctly */}
        <div style={{ margin:"auto", flex:"none", position:"relative", width:`${canvasSize.w*canvasScale}px`, height:`${canvasSize.h*canvasScale}px`, padding:"60px", boxSizing:"content-box" }}>
        <div
          ref={canvasRef}
          onMouseDown={onCanvasDown}
          onContextMenu={onCanvasContextMenu}
          onDoubleClick={onCanvasDbl}
          style={{
            width: `${canvasSize.w}px`, height: `${canvasSize.h}px`, position:"absolute", left:"60px", top:"60px",
            transform: `scale(${canvasScale})`,
            transformOrigin: "0 0",
            backgroundColor: boardBg,
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
            backgroundRepeat: "repeat",
            boxShadow: lightBoard
              ? "0 0 0 1px rgba(0,0,0,.25), 0 18px 70px rgba(0,0,0,.45)"
              : "0 0 0 1px rgba(255,255,255,.22), 0 18px 70px rgba(0,0,0,.5)",
            cursor: linkMode ? "crosshair" : "default",
            userSelect: "none",
          }}
        >
          {/* Canvas size watermark */}
          <div style={{ position:"absolute", bottom:"10px", right:"12px", fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.07)", letterSpacing:".08em", pointerEvents:"none", userSelect:"none", zIndex:1 }}>
            {canvasSize.w} × {canvasSize.h}
          </div>

          {/* Links — smooth multi-point connectors (curves sit behind images) */}
          <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none", zIndex:1, overflow:"visible" }}>
            {links.map((lk,li)=>{
              const pts = linkPoints(lk, items); if(!pts) return null;
              const d = smoothPath(pts);
              const isSel = selLink===li;
              return (
                <g key={li}>
                  <path d={d} stroke="transparent" strokeWidth="16" fill="none" style={{ pointerEvents: linkMode?"none":"stroke", cursor:"pointer" }}
                    onMouseDown={e=>{ if(linkMode||e.button===2) return; e.stopPropagation(); setSelLink(li); setSelected([]); }}/>
                  <path d={d} stroke={isSel?linkSel:linkSoft} strokeWidth={isSel?2.5:1.5} fill="none" strokeDasharray={isSel?"none":"6,4"} strokeLinecap="round" style={{ pointerEvents:"none" }}/>
                </g>
              );
            })}
            {/* in-progress draw preview */}
            {drawing && (() => {
              const ia = items.find(i=>i.id===drawing.aId); if(!ia) return null;
              const start = { x: ia.x + drawing.aOff.dx, y: ia.y + drawing.aOff.dy };
              const pts = [start, ...drawing.mids, drawing.cursor];
              return <>
                <path d={smoothPath(pts)} stroke={linkSel} strokeWidth="2" fill="none" strokeDasharray="5,4" strokeLinecap="round" style={{ pointerEvents:"none", opacity:.9 }}/>
                {[start, ...drawing.mids].map((p,i)=><circle key={i} cx={p.x} cy={p.y} r="4" fill={linkSel} stroke={linkHandleBg} strokeWidth="1.5" style={{ pointerEvents:"none" }}/>)}
              </>;
            })()}
          </svg>

          {/* Link edit handles — overlay ABOVE images so anchors stay grabbable */}
          {selLink!==null && !linkMode && links[selLink] && (() => {
            const pts = linkPoints(links[selLink], items); if(!pts) return null;
            const A = pts[0], B = pts[pts.length-1], mids = pts.slice(1,-1);
            const mx=(A.x+B.x)/2, my=(A.y+B.y)/2;
            return (
              <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none", zIndex:30, overflow:"visible" }}>
                {/* endpoints (snap to images) */}
                <circle cx={A.x} cy={A.y} r="6" fill={linkHandleBg} stroke={linkSel} strokeWidth="2" style={{ pointerEvents:"auto", cursor:"crosshair" }}
                  onMouseDown={e=>{ if(e.button===2) return; e.stopPropagation(); dragRef.current={ type:"linkA", li:selLink }; }}/>
                <circle cx={B.x} cy={B.y} r="6" fill={linkHandleBg} stroke={linkSel} strokeWidth="2" style={{ pointerEvents:"auto", cursor:"crosshair" }}
                  onMouseDown={e=>{ if(e.button===2) return; e.stopPropagation(); dragRef.current={ type:"linkB", li:selLink }; }}/>
                {/* mid anchors (float) — drag to move, right-click to remove */}
                {mids.map((m,k)=>(
                  <rect key={k} x={m.x-5} y={m.y-5} width="10" height="10" fill={linkHandleBg} stroke={linkSel} strokeWidth="1.5" style={{ pointerEvents:"auto", cursor:"move" }}
                    onMouseDown={e=>{ if(e.button===2) return; e.stopPropagation(); dragRef.current={ type:"linkMid", li:selLink, k }; }}
                    onContextMenu={e=>{ e.preventDefault(); e.stopPropagation(); setLinks(ls=>ls.map((lk,idx)=>{ if(idx!==selLink) return lk; const cur=lk[2]; const np={ a:{...cur.a}, b:{...cur.b}, mids:(cur.mids||[]).filter((_,j)=>j!==k) }; return [lk[0],lk[1],np]; })); }}/>
                ))}
                {/* delete whole link */}
                <g style={{ pointerEvents:"auto", cursor:"pointer" }} onMouseDown={e=>{ if(e.button===2) return; e.stopPropagation(); setLinks(ls=>ls.filter((_,i)=>i!==selLink)); setSelLink(null); }}>
                  <circle cx={mx} cy={my} r="9" fill="#1a0c10" stroke="rgba(255,90,90,.7)" strokeWidth="1.5"/>
                  <path d={`M ${mx-3} ${my-3} L ${mx+3} ${my+3} M ${mx+3} ${my-3} L ${mx-3} ${my+3}`} stroke="rgba(255,120,120,.9)" strokeWidth="1.5" strokeLinecap="round"/>
                </g>
              </svg>
            );
          })()}

          {/* Box selection */}
          {boxRect && (boxRect.width>4||boxRect.height>4) && (
            <div style={{ position:"absolute", left:boxRect.left, top:boxRect.top, width:boxRect.width, height:boxRect.height, border:"1px dashed rgba(232,255,71,.6)", background:"rgba(232,255,71,.04)", pointerEvents:"none", zIndex:200 }}/>
          )}

          {/* ── Live collaborator cursors ── */}
          {live && otherPeers.map(p => {
            const cur = cursors[p.id]; if (!cur) return null;
            return (
              <div key={`cur-${p.id}`} style={{ position:"absolute", left:cur.x, top:cur.y, zIndex:500, pointerEvents:"none", transform:`scale(${1/canvasScale})`, transformOrigin:"top left", transition:"left .08s linear, top .08s linear" }}>
                <svg width="20" height="22" viewBox="0 0 20 22" fill="none" style={{ filter:"drop-shadow(0 1px 2px rgba(0,0,0,.6))" }}>
                  <path d="M2 2 L2 16 L6 12 L9 19 L11.5 18 L8.5 11 L14 11 Z" fill={p.color} stroke="#060610" strokeWidth="1.2" strokeLinejoin="round"/>
                </svg>
                <div style={{ marginTop:"2px", marginLeft:"10px", padding:"1px 7px", borderRadius:"4px", background:p.color, color:"#060610", fontFamily:"'DM Mono'", fontSize:"9px", fontWeight:600, letterSpacing:".04em", whiteSpace:"nowrap" }}>
                  {p.name}
                </div>
              </div>
            );
          })}

          {/* ── Image items ── */}
          {imageItems.map(item => {
            const isSel  = selected.includes(item.id);
            const zoom   = imgZoom[item.id] || 1;
            const pan    = imgOffset[item.id] || { x: 0, y: 0 };
            // Clamp pan so image doesn't go out of frame when zoomed
            const maxPan = item.w * (zoom - 1) / 2;
            const cx     = Math.max(-maxPan, Math.min(maxPan, pan.x));
            const cy     = Math.max(-maxPan, Math.min(maxPan, pan.y));
            return (
              <div key={item.id}
                data-item-id={item.id}
                style={{ position:"absolute", left:item.x, top:item.y, width:item.w, zIndex:isSel?10:2, borderRadius:"7px", overflow:"hidden", outline:isSel?"2px solid #E8FF47":drawing?.aId===item.id?"2px solid rgba(232,255,71,.5)":"1px solid rgba(255,255,255,.07)", boxShadow:isSel?"0 0 0 4px rgba(232,255,71,.1)":"none", cursor:e=>e.altKey?"crosshair":linkMode?"crosshair":"move" }}
                onMouseDown={e => linkMode ? onLinkItemDown(e, item) : onItemDown(e, item)}
                onTouchStart={e => {
                  if (e.touches.length !== 1) return;
                  const t = e.touches[0];
                  const synth = { clientX: t.clientX, clientY: t.clientY, altKey: false, shiftKey: false, preventDefault: () => e.preventDefault(), stopPropagation: () => e.stopPropagation() };
                  if (!linkMode) onItemDown(synth, item);
                }}
                onWheel={e => onImgWheel(e, item)}
              >
                <img src={item.url} alt={item.tag} draggable={false}
                  style={{ width:"100%", display:"block", transform:`scale(${zoom}) translate(${cx/zoom}px, ${cy/zoom}px)`, transformOrigin:"center center", pointerEvents:"none", userSelect:"none" }}
                />
                {/* Zoom badge */}
                {isSel && zoom !== 1 && (
                  <div style={{ position:"absolute", top:"5px", left:"5px", padding:"2px 6px", borderRadius:"4px", background:"rgba(0,0,0,.7)", fontFamily:"'DM Mono'", fontSize:"8px", color:"rgba(255,255,255,.6)", pointerEvents:"none" }}>
                    {Math.round(zoom*100)}%
                  </div>
                )}
                {/* Reset zoom */}
                {isSel && zoom !== 1 && (
                  <button onMouseDown={e=>{e.stopPropagation();setImgZoom(z=>({...z,[item.id]:1}));}} style={{ position:"absolute", top:"5px", right:"5px", padding:"2px 7px", borderRadius:"4px", border:"none", background:"rgba(0,0,0,.75)", color:"rgba(255,255,255,.55)", fontFamily:"'DM Mono'", fontSize:"8px", cursor:"pointer" }}>reset</button>
                )}
                {/* Tag */}
                <div style={{ position:"absolute", bottom:"-20px", left:0, fontFamily:"'DM Mono'", fontSize:"9px", color:isSel?"rgba(232,255,71,.5)":"rgba(255,255,255,.22)", letterSpacing:".08em", textTransform:"uppercase", whiteSpace:"nowrap", pointerEvents:"none" }}>{item.tag}</div>
              </div>
            );
          })}

          {/* ── Resize handles (outside overflow:hidden) ── */}
          {imageItems.filter(i=>selected.includes(i.id)&&selected.length===1).map(item=>(
            <div key={`rh-${item.id}`}
              onMouseDown={e=>onResizeDown(e,item)}
              style={{ position:"absolute", left:item.x+item.w-10, top:item.y+item.w*0.55, width:"22px", height:"22px", borderRadius:"6px", background:"#E8FF47", cursor:"se-resize", zIndex:30, boxShadow:"0 2px 10px rgba(0,0,0,.6)", display:"flex", alignItems:"center", justifyContent:"center" }}
            >
              <svg width="9" height="9" viewBox="0 0 8 8"><path d="M1 7L7 1M4 7L7 4" stroke="#070710" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </div>
          ))}

          {/* ── Text items ── */}
          {textItems.map(item => {
            const isSel = selected.includes(item.id);
            return (
              <div key={item.id}
                style={{ position:"absolute", left:item.x, top:item.y, width:item.w, zIndex:isSel?10:3, outline:isSel?"1px dashed rgba(232,255,71,.5)":"1px dashed rgba(255,255,255,.07)", borderRadius:"4px", padding:"6px 8px", cursor:"move", minHeight:"32px", background:item.bg||"transparent" }}
                onMouseDown={e => onItemDown(e,item)}
                onDoubleClick={e=>{ e.stopPropagation(); upd(item.id,{editing:true}); setSelected([item.id]); }}
              >
                {item.editing ? (
                  <textarea autoFocus value={item.content} onChange={e=>upd(item.id,{content:e.target.value})}
                    onBlur={()=>upd(item.id,{editing:false})}
                    onKeyDown={e=>{ if(e.key==="Escape")upd(item.id,{editing:false}); e.stopPropagation(); }}
                    placeholder="Type your note..."
                    style={{ width:"100%", minHeight:"60px", background:"transparent", border:"none", outline:"none", resize:"none", fontStyle:item.italic?"italic":"normal", fontWeight:item.bold?"bold":"normal", fontSize:`${item.fontSize}px`, color:item.color, textAlign:item.align, lineHeight:"1.5", cursor:"text", fontFamily:"'DM Sans',sans-serif" }}
                  />
                ) : (
                  <p style={{ fontFamily:"'DM Sans',sans-serif", fontStyle:item.italic?"italic":"normal", fontWeight:item.bold?"bold":"normal", fontSize:`${item.fontSize}px`, color:item.content?item.color:"rgba(255,255,255,.2)", textAlign:item.align, lineHeight:"1.5", whiteSpace:"pre-wrap", wordBreak:"break-word", margin:0, minHeight:"24px" }}>
                    {item.content||"Double-click to edit"}
                  </p>
                )}
                {isSel && <div onMouseDown={e=>{e.stopPropagation();pushHistory(items);dragRef.current={type:"resize",id:item.id,sx:e.clientX,sw:item.w};}} style={{ position:"absolute", bottom:"-8px", right:"-8px", width:"16px", height:"16px", borderRadius:"4px", background:"rgba(232,255,71,.6)", cursor:"se-resize", zIndex:20 }}/>}
              </div>
            );
          })}

          {items.length===0 && (
            <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", textAlign:"center" }}>
              <p style={{ fontFamily:"'Bebas Neue'", fontSize:"36px", letterSpacing:".15em", color:"rgba(255,255,255,.06)" }}>EMPTY CANVAS</p>
              <p style={{ fontFamily:"'DM Mono'", fontSize:"11px", color:"rgba(255,255,255,.15)", marginTop:"8px" }}>Go back and select images - double-click canvas to add text</p>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* ── Save modal ── */}
      {showSave && (() => {
        const [overwriteId, setOverwriteId] = [currentMoodId, setCurrentMoodId]; // alias for clarity
        const handleDoSave = (targetId) => {
          const id   = targetId || Date.now();
          const name = moodName || (targetId ? savedMoodboards?.find(m=>m.id===targetId)?.name : null) || "Moodboard";
          const mb   = { id, name, items, links, imgZoom, imgOffset, canvasSize, boardBg, savedAt: new Date().toLocaleDateString() };
          onSaveMoodboard(mb);
          setCurrentMoodId(id);
          setMoodName(name);
          setShowSave(false);
        };
        return (
          <div onClick={()=>setShowSave(false)} style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(0,0,0,.85)", backdropFilter:"blur(12px)", display:"flex", alignItems:"center", justifyContent:"center", animation:"fade-in .2s ease", padding:"20px" }}>
            <div onClick={e=>e.stopPropagation()} style={{ width:"460px", maxHeight:"85vh", overflowY:"auto", background:"#0b0b1c", border:"1px solid rgba(255,255,255,.08)", borderRadius:"16px", padding:"28px", boxShadow:"0 40px 100px rgba(0,0,0,.9)", animation:"panel-in .25s ease" }}>
              <p style={{ fontFamily:"'Bebas Neue'", fontSize:"22px", letterSpacing:".15em", color:"#E8FF47", marginBottom:"6px" }}>SAVE MOODBOARD</p>
              <p style={{ fontFamily:"'DM Mono'", fontSize:"10px", color:"rgba(255,255,255,.28)", marginBottom:"18px" }}>{items.length} items - layout + zoom + text saved</p>

              {/* B27: guest in a live session — edits already persist to the owner's board */}
              {live && !isOwnerNow && (
                <div style={{ marginBottom:"18px", padding:"12px 14px", borderRadius:"10px", background:"rgba(94,211,243,.07)", border:"1px solid rgba(94,211,243,.25)" }}>
                  <p style={{ fontFamily:"'DM Mono'", fontSize:"9px", letterSpacing:".08em", textTransform:"uppercase", color:"#5ED3F3", marginBottom:"6px" }}>Live session</p>
                  <p style={{ fontFamily:"'DM Sans'", fontSize:"12px", lineHeight:"1.6", color:"rgba(255,255,255,.6)" }}>
                    You're editing {moodName ? `"${moodName}"` : "a shared board"}{roomOwnerEmail ? ` (owned by ${roomOwnerEmail})` : ""}. Your changes save to the owner's board automatically — you don't need to save. Use the options below only if you want a separate personal copy in your own library.
                  </p>
                </div>
              )}

              {/* ── Save as new ── */}
              <p style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.3)", letterSpacing:".1em", textTransform:"uppercase", marginBottom:"8px" }}>Save as new</p>
              <div style={{ display:"flex", gap:"8px", marginBottom:"22px" }}>
                <input value={moodName} onChange={e=>setMoodName(e.target.value)} autoFocus
                  onKeyDown={e=>{ if(e.key==="Enter") handleDoSave(null); }}
                  placeholder="Name this moodboard..."
                  style={{ flex:1, padding:"11px 14px", borderRadius:"8px", background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.12)", color:"#fff", fontFamily:"'DM Mono'", fontSize:"12px", outline:"none" }}
                />
                <button onClick={()=>handleDoSave(null)} style={{ padding:"11px 18px", borderRadius:"8px", border:"none", background:"#E8FF47", color:"#070710", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer", fontWeight:"600", whiteSpace:"nowrap" }}>+ New</button>
              </div>

              {/* ── Overwrite existing ── */}
              {savedMoodboards?.length > 0 && (
                <>
                  <p style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.3)", letterSpacing:".1em", textTransform:"uppercase", marginBottom:"10px" }}>Overwrite existing</p>
                  <div style={{ display:"flex", flexDirection:"column", gap:"7px" }}>
                    {savedMoodboards.map(mb => (
                      <div key={mb.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", borderRadius:"9px", background: currentMoodId===mb.id?"rgba(232,255,71,.07)":"rgba(255,255,255,.03)", border: currentMoodId===mb.id?"1px solid rgba(232,255,71,.25)":"1px solid rgba(255,255,255,.07)" }}>
                        <div>
                          {renamingMbId === mb.id ? (
                            <input autoFocus value={renamingMbVal}
                              onChange={e=>setRenamingMbVal(e.target.value)}
                              onBlur={()=>{ if(renamingMbVal.trim()) onSaveMoodboard({...mb,name:renamingMbVal.trim()}); setRenamingMbId(null); }}
                              onKeyDown={e=>{ if(e.key==="Enter"){ if(renamingMbVal.trim()) onSaveMoodboard({...mb,name:renamingMbVal.trim()}); setRenamingMbId(null); } if(e.key==="Escape") setRenamingMbId(null); }}
                              style={{ fontFamily:"'DM Mono'", fontSize:"11px", color:"#E8FF47", background:"rgba(232,255,71,.06)", border:"1px solid rgba(232,255,71,.3)", borderRadius:"4px", padding:"2px 6px", outline:"none", width:"180px" }}
                            />
                          ) : (
                            <p onDoubleClick={()=>{ setRenamingMbId(mb.id); setRenamingMbVal(mb.name||""); }} title="Double-click to rename"
                              style={{ fontFamily:"'DM Mono'", fontSize:"11px", color: currentMoodId===mb.id?"rgba(232,255,71,.9)":"rgba(255,255,255,.65)", marginBottom:"2px", cursor:"text" }}>{mb.name}</p>
                          )}
                          <p style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.25)" }}>{mb.items?.length||0} items - {mb.savedAt}</p>
                        </div>
                        <button onClick={()=>handleDoSave(mb.id)} style={{ padding:"7px 14px", borderRadius:"7px", border:"1px solid rgba(232,255,71,.35)", background:"rgba(232,255,71,.08)", color:"#E8FF47", fontFamily:"'DM Mono'", fontSize:"9px", letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer", whiteSpace:"nowrap" }}>↻ Overwrite</button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <button onClick={()=>setShowSave(false)} style={{ width:"100%", marginTop:"18px", padding:"9px", borderRadius:"8px", border:"1px solid rgba(255,255,255,.08)", background:"transparent", color:"rgba(255,255,255,.25)", fontFamily:"'DM Mono'", fontSize:"10px", cursor:"pointer" }}>Cancel</button>
            </div>
          </div>
        );
      })()}

      {/* ── Load modal ── */}
      {showLoad && (
        <div onClick={()=>setShowLoad(false)} style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(0,0,0,.85)", backdropFilter:"blur(12px)", display:"flex", alignItems:"center", justifyContent:"center", animation:"fade-in .2s ease" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"480px", maxHeight:"80vh", overflowY:"auto", background:"#0b0b1c", border:"1px solid rgba(255,255,255,.08)", borderRadius:"16px", padding:"28px", boxShadow:"0 40px 100px rgba(0,0,0,.9)", animation:"panel-in .25s ease" }}>
            <p style={{ fontFamily:"'Bebas Neue'", fontSize:"22px", letterSpacing:".15em", color:"#E8FF47", marginBottom:"20px" }}>SAVED MOODBOARDS</p>
            <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
              {savedMoodboards.map(mb=>(
                <div key={mb.id} style={{ padding:"14px 16px", borderRadius:"10px", background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div>
                    {renamingMbId === mb.id ? (
                          <input autoFocus value={renamingMbVal}
                            onChange={e=>setRenamingMbVal(e.target.value)}
                            onBlur={()=>{ if(renamingMbVal.trim()) onSaveMoodboard({...mb,name:renamingMbVal.trim()}); setRenamingMbId(null); }}
                            onKeyDown={e=>{ if(e.key==="Enter"){ if(renamingMbVal.trim()) onSaveMoodboard({...mb,name:renamingMbVal.trim()}); setRenamingMbId(null); } if(e.key==="Escape") setRenamingMbId(null); }}
                            style={{ fontFamily:"\'DM Mono\'", fontSize:"11px", color:"#E8FF47", background:"rgba(232,255,71,.06)", border:"1px solid rgba(232,255,71,.3)", borderRadius:"4px", padding:"2px 6px", outline:"none", width:"180px" }}
                          />
                        ) : (
                          <p onDoubleClick={()=>{ setRenamingMbId(mb.id); setRenamingMbVal(mb.name||""); }} title="Double-click to rename"
                            style={{ fontFamily:"\'DM Mono\'", fontSize:"11px", color:"rgba(255,255,255,.75)", marginBottom:"3px", cursor:"text" }}>{mb.name}</p>
                        )}
                    <p style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.25)" }}>{mb.items?.length||0} items - {mb.savedAt}</p>
                  </div>
                  <div style={{ display:"flex", gap:"8px" }}>
                    <button onClick={()=>{ onLoadMoodboard(mb); if(mb.imgZoom) setImgZoom(mb.imgZoom); setLinks(mb.links||[]); setImgOffset(mb.imgOffset||{}); if(mb.canvasSize) setCanvasSize(mb.canvasSize); if(mb.boardBg) setBoardBg(mb.boardBg); setSelLink(null); setCurrentMoodId(mb.id); setMoodName(mb.name||""); setShowLoad(false); }} style={{ padding:"7px 14px", borderRadius:"6px", border:"none", background:"#E8FF47", color:"#070710", fontFamily:"'DM Mono'", fontSize:"9px", letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer", fontWeight:"500" }}>Load</button>
                    <button onClick={()=>onDeleteMoodboard(mb.id)} style={{ padding:"7px 10px", borderRadius:"6px", border:"1px solid rgba(255,80,80,.2)", background:"transparent", color:"rgba(255,100,100,.5)", fontFamily:"'DM Mono'", fontSize:"9px", cursor:"pointer" }}>×</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* ── Share session modal ── */}
      {showShare && live && (
        <div onClick={()=>setShowShare(false)} style={{ position:"fixed", inset:0, zIndex:650, background:"rgba(0,0,0,.85)", backdropFilter:"blur(12px)", display:"flex", alignItems:"center", justifyContent:"center", animation:"fade-in .2s ease", padding:"20px" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"460px", maxWidth:"100%", background:"#0b0b1c", border:"1px solid rgba(94,211,243,.18)", borderRadius:"16px", padding:"28px", boxShadow:"0 40px 100px rgba(0,0,0,.9)", animation:"panel-in .25s ease" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px" }}>
              <span style={{ width:"8px", height:"8px", borderRadius:"50%", background:"#7CF6A9", animation:"pulse 1.5s ease-in-out infinite" }}/>
              <p style={{ fontFamily:"'Bebas Neue'", fontSize:"22px", letterSpacing:".15em", color:"#5ED3F3" }}>LIVE SESSION</p>
            </div>
            <p style={{ fontFamily:"'DM Mono'", fontSize:"10px", color:"rgba(255,255,255,.32)", lineHeight:"1.7", marginBottom:"18px" }}>
              Anyone signed in to ORACLE with this link can join and edit this moodboard with you in real time. {peers.length} {peers.length===1?"person":"people"} here now.
            </p>

            <p style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.3)", letterSpacing:".1em", textTransform:"uppercase", marginBottom:"8px" }}>Share link</p>
            <div style={{ display:"flex", gap:"8px", marginBottom:"18px" }}>
              <input readOnly value={sessionUrl||""} onFocus={e=>e.target.select()}
                style={{ flex:1, padding:"11px 12px", borderRadius:"8px", background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.12)", color:"rgba(255,255,255,.7)", fontFamily:"'DM Mono'", fontSize:"11px", outline:"none", overflow:"hidden", textOverflow:"ellipsis" }}/>
              <button onClick={copyLink} style={{ padding:"11px 18px", borderRadius:"8px", border:"none", background: copied?"#7CF6A9":"#5ED3F3", color:"#060610", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer", fontWeight:600, whiteSpace:"nowrap", transition:"background .2s" }}>{copied?"Copied":"Copy"}</button>
            </div>

            <div style={{ display:"flex", flexWrap:"wrap", gap:"8px", marginBottom:"22px" }}>
              {peers.map(p => (
                <div key={p.id} style={{ display:"flex", alignItems:"center", gap:"7px", padding:"5px 10px 5px 6px", borderRadius:"20px", background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.08)" }}>
                  <div style={{ width:"20px", height:"20px", borderRadius:"50%", border:`2px solid ${p.color}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <span style={{ fontFamily:"'DM Mono'", fontSize:"8px", color:p.color, fontWeight:600 }}>{(p.name||"?").slice(0,1).toUpperCase()}</span>
                  </div>
                  <span style={{ fontFamily:"'DM Mono'", fontSize:"10px", color:"rgba(255,255,255,.6)" }}>{p.name}{p.id===selfIdRef.current?" (you)":""}</span>
                </div>
              ))}
            </div>

            <div style={{ display:"flex", gap:"10px" }}>
              <button onClick={()=>setShowShare(false)} style={{ flex:1, padding:"11px", borderRadius:"8px", border:"1px solid rgba(255,255,255,.1)", background:"transparent", color:"rgba(255,255,255,.5)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>Keep editing</button>
              <button onClick={leaveSession} style={{ padding:"11px 18px", borderRadius:"8px", border:"1px solid rgba(255,80,80,.25)", background:"rgba(255,60,60,.07)", color:"rgba(255,120,120,.8)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".06em", textTransform:"uppercase", cursor:"pointer" }}>End / Leave</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile portrait: nudge to rotate ── */}
      {isMobile && (
        <div className="oracle-portrait-only" style={{ position:"fixed", inset:0, zIndex:800, background:"rgba(7,7,16,.97)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"20px", padding:"32px" }}>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect x="8" y="4" width="28" height="40" rx="4" stroke="rgba(232,255,71,.6)" strokeWidth="2"/>
            <path d="M44 16 L38 10 L38 22 Z" fill="rgba(232,255,71,.6)"/>
            <path d="M38 16 L20 16" stroke="rgba(232,255,71,.6)" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <div style={{ textAlign:"center" }}>
            <p style={{ fontFamily:"'Bebas Neue'", fontSize:"24px", letterSpacing:".15em", color:"#E8FF47", marginBottom:"8px" }}>ROTATE DEVICE</p>
            <p style={{ fontFamily:"'DM Mono'", fontSize:"10px", color:"rgba(255,255,255,.35)", lineHeight:"1.7", letterSpacing:".05em" }}>Moodboard works best in landscape mode. Rotate your device horizontally for the full experience.</p>
          </div>
          <button onClick={onBack} style={{ padding:"10px 24px", borderRadius:"8px", border:"1px solid rgba(255,255,255,.12)", background:"transparent", color:"rgba(255,255,255,.4)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".1em", textTransform:"uppercase", cursor:"pointer" }}>← Back to Library</button>
          <button onClick={e=>{e.currentTarget.closest(".oracle-portrait-only").style.display="none";}} style={{ padding:"10px 24px", borderRadius:"8px", border:"1px solid rgba(232,255,71,.3)", background:"rgba(232,255,71,.08)", color:"#E8FF47", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".1em", textTransform:"uppercase", cursor:"pointer" }}>Continue Anyway</button>
        </div>
      )}

      {/* ── Mobile: bottom action bar when items selected ── */}
      {isMobile && selected.length > 0 && (
        <div style={{ position:"fixed", bottom:"env(safe-area-inset-bottom, 16px)", left:"50%", transform:"translateX(-50%)", zIndex:300, background:"rgba(10,10,27,.97)", border:"1px solid rgba(255,255,255,.12)", borderRadius:"14px", padding:"10px 16px", display:"flex", gap:"10px", alignItems:"center", boxShadow:"0 8px 32px rgba(0,0,0,.7)", backdropFilter:"blur(20px)" }}>
          <span style={{ fontFamily:"'DM Mono'", fontSize:"10px", color:"rgba(255,255,255,.4)", letterSpacing:".06em" }}>{selected.length} selected</span>
          <button onClick={() => { pushHistory(items); setItems(p => { const sel = p.filter(i=>selected.includes(i.id)); const copies = sel.map(i=>({...i,id:Date.now()+Math.random(),x:i.x+24,y:i.y+24})); return [...p,...copies]; }); setSelected([]); }} style={{ padding:"7px 12px", borderRadius:"8px", border:"1px solid rgba(255,255,255,.12)", background:"rgba(255,255,255,.06)", color:"rgba(255,255,255,.7)", fontFamily:"'DM Mono'", fontSize:"9px", letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer" }}>Dup</button>
          <button onClick={() => { if(selected.length && window.confirm("Remove selected?")) { const next = items.filter(i=>!selected.includes(i.id)); pushHistory(items); setItems(next); setSelected([]); } }} style={{ padding:"7px 12px", borderRadius:"8px", border:"1px solid rgba(255,80,80,.2)", background:"rgba(255,50,50,.08)", color:"rgba(255,120,120,.8)", fontFamily:"'DM Mono'", fontSize:"9px", letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer" }}>Del</button>
          <button onClick={() => setSelected([])} style={{ padding:"7px 10px", borderRadius:"8px", border:"1px solid rgba(255,255,255,.08)", background:"transparent", color:"rgba(255,255,255,.3)", fontFamily:"'DM Mono'", fontSize:"11px", cursor:"pointer" }}>×</button>
        </div>
      )}

      {/* ── Export Preview Modal ── */}
      {exportPreview && (
        <div style={{ position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,.92)", backdropFilter:"blur(16px)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", animation:"fade-in .25s ease", padding:"24px" }}>
          {/* Header */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", maxWidth:"900px", marginBottom:"16px" }}>
            <div>
              <p style={{ fontFamily:"'Bebas Neue'", fontSize:"20px", letterSpacing:".14em", color:"#E8FF47" }}>EXPORT PREVIEW</p>
              <p style={{ fontFamily:"'DM Mono'", fontSize:"9px", color:"rgba(255,255,255,.3)", marginTop:"2px" }}>{exportPreview.w} × {exportPreview.h}px - PNG</p>
            </div>
            <div style={{ display:"flex", gap:"10px", alignItems:"center" }}>
              <a
                href={exportPreview.dataUrl}
                download="oracle-moodboard.png"
                style={{ padding:"9px 22px", borderRadius:"8px", border:"none", background:"#E8FF47", color:"#070710", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer", fontWeight:"600", textDecoration:"none", display:"inline-block" }}
              >
                ↓ Download PNG
              </a>
              <button onClick={()=>setExportPreview(null)} style={{ padding:"9px 16px", borderRadius:"8px", border:"1px solid rgba(255,255,255,.12)", background:"transparent", color:"rgba(255,255,255,.45)", fontFamily:"'DM Mono'", fontSize:"10px", cursor:"pointer" }}>Close</button>
            </div>
          </div>
          {/* Preview image — checkerboard backdrop so the canvas edge is always visible */}
          <div style={{ width:"100%", maxWidth:"900px", borderRadius:"12px", overflow:"hidden", border:"1px solid rgba(255,255,255,.08)", boxShadow:"0 40px 100px rgba(0,0,0,.9)", flex:1, display:"flex", alignItems:"center", justifyContent:"center",
            backgroundColor:"#2a2a33",
            backgroundImage:"linear-gradient(45deg, #20202a 25%, transparent 25%), linear-gradient(-45deg, #20202a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #20202a 75%), linear-gradient(-45deg, transparent 75%, #20202a 75%)",
            backgroundSize:"24px 24px", backgroundPosition:"0 0, 0 12px, 12px -12px, -12px 0px", padding:"28px" }}>
            <img
              src={exportPreview.dataUrl}
              alt="Moodboard preview"
              style={{ maxWidth:"100%", maxHeight:"66vh", display:"block", objectFit:"contain", border:"1px solid rgba(255,255,255,.35)", boxShadow:"0 0 0 1px rgba(0,0,0,.5), 0 20px 60px rgba(0,0,0,.7)" }}
            />
          </div>
        </div>
      )}

    </div>
  );
}
